/**
 * The bench, drawn.
 *
 * One SVG in bench coordinates — metres, ceiling at y = 0, down positive —
 * exactly the coordinates the engine works in, so nothing is converted between
 * the physics and the picture and no sign can drift between them.
 *
 * Every point this file draws to comes from the engine: a part's joining
 * points are `portsOf`, a rope's stops are `segmentsOf`, and which rim a run
 * leaves from is `effectivePort`. The drawing adds exactly one thing of its
 * own — it lifts the rope a rope's-width proud of the rim so the wrap can be
 * seen — and that offset is the only number here the model does not know
 * about.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { mechanics as M } from '@padcanvas/shared';
import { mechanics } from '@padcanvas/shared';
import { fixed, kg } from './format';

const {
    partById, portsOf, portAt, frameOf, outward, ropesOf, bondsOf, defOf, joinNeed, openEndOf, joinsAt,
    WEIGHT_HALF_W, bodiesOf, BALANCE_HALF, spansOf, spanId, segmentsOf, tensionNames, released, anyPinned,
} = mechanics;

export type World = M.World;
export type Part = M.Part;
export type Port = M.Port;
export type PortRef = M.PortRef;

/** The key identifies the POINT, not the part: a sheave has three of them and
 *  two are rims, so "which port is picked" cannot be answered by a part id —
 *  asked that way, clicking one rim lit up both. */
export const portKey = (ref: PortRef): string => `${ref.partId}#${ref.portId}`;

/**
 * Where the rope's centreline runs, relative to the sheave.
 *
 * Half the rim's stroke — which puts the centreline exactly on the sheave's
 * painted outer edge, so half the rope's width shows over the wheel and half
 * is covered by it. That is a rope sitting IN a groove. Clear of the rim the
 * whole thickness stood proud, as if the rope were resting on top of the
 * wheel; buried at exactly r it vanished under the rim's own line and the wrap
 * — the thing that makes a pulley a pulley — could not be seen at all.
 *
 * Drawing only. The engine's contact point is the tangent at exactly r, which
 * is what the joining points and the alignment arithmetic use.
 */
const ROPE_LIFT = 0.013;

/** A closer look at the room: `k` times the room's scale, centred on (cx, cy)
 *  in the drawing's own coordinates (y down, as the viewBox has it). */
export interface SceneZoom { k: number; cx: number; cy: number }

export interface SceneProps {
    /** What to draw: the bench as it stands, or as it is at the cursor. */
    world: World;
    /** Absent or null: the whole room, as the bench has always drawn it. Only a
     *  host that passes `onZoom` lets the reader pinch and pan. */
    zoom?: SceneZoom | null;
    onZoom?: (z: SceneZoom | null) => void;
    /** The smallest a joining point's or a handle's target may be, in screen px
     *  of radius — a finger's, on a phone; absent, the metric size alone. */
    touchPx?: number;
    /** A finger held still on a part: what a double-click does with a mouse. */
    onPartLongPress?: (id: string) => void;
    /** Each new value zooms to frame the machine — its parts, and a margin —
     *  through `onZoom`. A phone's opening view, its Fit, and a loaded arrangement. */
    fitKey?: number;
    /**
     * Where a part is being dragged TO, while the finger is still down.
     *
     * The bench itself is not touched until the finger comes off. A drag used
     * to move the part for real on every pointermove, which meant the reader
     * watched ropes go slack and wheels sit where nothing holds them — a bench
     * in a state the physics disowns, on its way to one it does not. This is
     * the part's outline at the cursor and nothing more: an intention, drawn
     * as an intention.
     */
    ghost: { id: string; at: { x: number; y: number }; angle?: number; targets?: M.Snap[]; snap?: M.Snap; preview?: M.World } | null;
    /** While it runs: each moving body's speed and acceleration, tagged on the
     *  part that carries it. Empty at rest. (Not `motion`, which is the arrows
     *  the Motion switch draws.) */
    motionTags?: { partId: string; name: string; mass: number; v: number; a: number }[];
    /** The bench is running (or stepped past the start): tags keep where they were
     *  put relative to their parts, and ride along with them, overlapping if so. */
    inMotion?: boolean;
    /** With Forces on: each mass's forces, by symbol, in x and y. */
    forceTags?: { partId: string; name: string; mass: number; rows: { label: string; x: number; y: number }[] }[];
    /** The forces at the cursor, or null while the bench is being built. */
    force: Record<string, number> | null;
    /** What each moving mass is accelerating at, while the bench is paused. */
    motion: Record<string, { x: number; y: number; size: number }> | null;
    /** The same shape, for velocity. Drawn beside the acceleration rather than
     *  on top of it: on a bench where everything falls straight down the two
     *  are collinear, and one arrow through another is one arrow. */
    speed: Record<string, { x: number; y: number; size: number }> | null;
    /** Every force on every body, when a reader has asked to see them. */
    arrows: {
        at: { x: number; y: number };
        vector: { x: number; y: number };
        kind: string;
        label: string;
        /** The part it acts on, so only the selected one's are numbered. */
        on?: string;
    }[] | null;
    /**
     * Whether forces are being SHOWN at all.
     *
     * Separate from `force`, which the bench needs whatever is switched on: a
     * slack rope is drawn slack because a rope with no tension is a fact about
     * the rope, and a spring balance reads its number because reading it is the
     * only thing a balance is for. What the switch governs is force ARROWS and
     * the tensions written beside them — everything that is the bench saying
     * "and here is a force", rather than the bench drawing itself.
     */
    showForces: boolean;
    problems: M.Problem[];
    selectedId: string | null;
    /** The first of the two points, once it has been picked. */
    first: PortRef | null;
    /** Whether the bench is picking two points to join. */
    joining: boolean;
    /** The points a join can use right now, by `portKey` — only these are drawn. */
    offered: Set<string>;
    /** A join has a starting point, so a surface can be pressed along its top. */
    picking: boolean;
    selectedLinkId: string | null;
    onLinkClick: (id: string) => void;
    onPort: (ref: PortRef) => void;
    /** A press on a free tail of a pulley's rope piece: a rope pulled out from that rim. */
    onTailPointerDown: (ref: PortRef, e: React.PointerEvent) => void;
    /** A rope being pulled out from a rim: the rim, where the hand is, what it could join.
     *  `rewire` when it is one end of a line already there being moved: that
     *  line is drawn faint underneath, and `from` is the stop the end hangs from. */
    pulling: { from: PortRef; to: { x: number; y: number }; targets: M.Snap[]; snap?: M.Snap; rewire?: { ropeId: string; atEnd: boolean } } | null;
    /** A press on a joining point: a join dragged out from there to the next. */
    onPortPointerDown?: (ref: PortRef, e: React.PointerEvent) => void;
    /** A press on one end of the selected rope or rod: tie that end somewhere else. */
    onRopeEndPointerDown?: (ropeId: string, atEnd: boolean, e: React.PointerEvent) => void;
    onPartPointerDown: (id: string, e: React.PointerEvent) => void;
    onPartClick: (id: string) => void;
    /** Double-click: for a part that can be resized, show its handles. */
    onPartDoubleClick: (id: string) => void;
    /** The part whose resize handles are showing. */
    resizing: string | null;
    onHandlePointerDown: (id: string, handle: string, e: React.PointerEvent) => void;
    onBackground: () => void;
    svgRef?: React.Ref<SVGSVGElement>;
}

/**
 * One scale of line weights, and one colour for "you can act on this".
 *
 * Every stroke was once set where it was drawn, so the bench had nine weights
 * between 0.02 and 0.055 and read as heavy — a rope no thinner than the sheave
 * it ran over, an annotation ring thicker than the part it marked. These are
 * the four weights it actually needs, and nothing outside them.
 *
 * The dashes are dots, not dashes: on a 0.46 ring, 0.026 on / 0.05 off with a
 * round cap gives about forty beads, which reads as a highlight rather than as
 * a second drawn part.
 */
/**
 * Turn a label the right way up where it stands.
 *
 * Everything is drawn y-up inside one flipped group, so text comes out
 * mirrored and has to be flipped back — about the line it is anchored on, which
 * for these labels (all `dominantBaseline="middle"`) is its middle.
 *
 * This was a stylesheet rule — `transform-box: fill-box; transform: scaleY(-1)`
 * on every `text` — and Safari draws no text at all under it: every mass, name,
 * tension and motion value vanished there while Chrome showed them. A
 * transform ATTRIBUTE is plain SVG and every browser honours it.
 */
const upright = (y: number): string => `matrix(1 0 0 -1 0 ${2 * y})`;

const W = { hair: 0.014, fine: 0.024, line: 0.032, rim: 0.026 };
const DASH = { ring: '0.026 0.05', slack: '0.09 0.075' };
/** Selected, selectable, being joined — all one colour. Three said three
 *  different things when they meant the same one. Red is kept for the only
 *  thing that is NOT a selection: something actually wrong. */
const HILITE = '#7fdba4';
/**
 * A joining point, by what it still needs.
 *
 * Grey it is done, green you may, red you must — and the red ones are exactly
 * the points whose absence the alert beneath the bench is already complaining
 * about, so the dot and the sentence are the same statement said twice, once
 * where the problem is and once in words.
 */
const PORT: Record<M.JoinNeed, string> = {
    joined: 'var(--mb-hook)',
    optional: HILITE,
    needed: 'var(--mb-alarm)',
};
const ALARM = 'var(--mb-alarm)';

/**
 * Three materials, three colours, none of them faint.
 *
 * A rope is the neutral high-contrast one, a strap is indigo like the sheave
 * it belongs to, a hanger is cyan like the weight. Nothing on this bench is a
 * faded version of anything else.
 */
const COLOUR = {
    rope: 'var(--mb-rope)',
    sheave: 'var(--mb-panel)',
    pulley: 'var(--mb-sheave)',
    weight: 'var(--mb-load)',
    anchor: 'var(--mb-hook)',
    hand: 'var(--mb-hand)',
    slab: 'var(--mb-slab-edge)',
};

const sheaveR = (p: Part): number => (p.data as { radius?: number }).radius ?? 0.3;
/** How far the rope piece on a free pulley hangs below its rim. Its tips are
 *  where the pulley's attach points are drawn and where a rope is pulled from. */
export const ROPE_PIECE_TAIL = 0.14;

/** Where a point is DRAWN for joining: a rim at the tip of the rope piece's tail,
 *  anything else where it is. */
function tipOf(w: World, ref: PortRef): { x: number; y: number } | undefined {
    const port = portAt(w, ref);
    if (!port) return undefined;
    return port.role === 'guide' ? { x: port.at.x, y: port.at.y - ROPE_PIECE_TAIL } : port.at;
}

/** The tails of a pulley's rope piece still free to pull a rope from: none once a
 *  rope runs over the wheel, and not a side a rope already holds. */
function freeTails(w: World, p: Part): string[] {
    if (ropesOf(w).some(r => r.path.slice(1, -1).some(s => s.partId === p.id))) return [];
    return ['rimL', 'rimR'].filter(id => joinsAt(w, { partId: p.id, portId: id }).length === 0);
}

/** The radius the rope runs at — the groove, which may be deeper than the disc. */
const grooveR = (p: Part): number => (p.data as { wrap?: number }).wrap ?? sheaveR(p);
const massOf = (p: Part): number => (p.data as { mass?: number }).mass ?? 0;
const pullOf = (p: Part): number | undefined => (p.data as { pull?: number }).pull;

/** A rope stop, lifted clear of the rim so the wrap shows. */
function contact(w: World, ref: PortRef): { x: number; y: number; rim: boolean } {
    const port = portAt(w, ref);
    if (!port) return { x: 0, y: 0, rim: false };
    if (port.role !== 'guide') return { x: port.at.x, y: port.at.y, rim: false };
    // Lifted OUT from the rim, whichever rim: sideways off a side, up off the top.
    const part = partById(w, ref.partId);
    const o = part ? outward(part, port) : { x: 1, y: 0 };
    return { x: port.at.x + o.x * ROPE_LIFT, y: port.at.y + o.y * ROPE_LIFT, rim: true };
}

/** One end of one run, and the way the rope pulls the part it is tied to. */
interface Pull { at: { x: number; y: number }; dir: { x: number; y: number } }

interface RopeDrawing {
    id: string;
    d: string;
    slack: boolean;
    /** Which way this rope pulls, at each place it is attached. */
    pulls: Pull[];
    at: { x: number; y: number };
    /** Which side of the label the rope is on. */
    anchor: 'end' | 'middle' | 'start';
    /** What its first span is carrying, or undefined when it is slack. */
    tension: number | undefined;
    /** A rod: it pushes as well as pulls, and has to look like it can. */
    rigid: boolean;
    /** Where it is plated, when it is a rod. */
    ends: { x: number; y: number }[];
    /** Set where the rope stops on a rim without ending. */
    openAt: { x: number; y: number } | null;
}

/**
 * Every rope as one path: straight runs, and an arc where it goes over a wheel.
 *
 * A run between a rim and anything else is drawn from the LIFTED rim x, and
 * the other end follows it — by less than a rope is thick. The alternative is
 * a rope that leans by two degrees, and a leaning rope is the one thing the
 * model says is not there.
 */
/**
 * How much rope each run has to spare, and how deep that makes it hang.
 *
 * Extra rope is a number in the panel and nothing on the bench, which is the
 * drawing asserting something the model denies — the same fault as a leaning
 * rope drawn straight. A rope 40 cm longer than its run has to LOOK 40 cm
 * longer.
 *
 * A shallow bow of depth `d` over a chord of length `L` is about
 * `L(1 + 8d²⁄3L²)` long, so the depth that uses up `extra` is `√(3·L·extra⁄8)`.
 * The surplus of a span is shared between its runs in proportion to their
 * length, because a slack rope does not pick one place to be loose.
 */
function sagOf(w: World): Map<string, number> {
    const out = new Map<string, number>();
    spansOf(w).forEach(span => {
        const extra = span.was - span.now;
        if (extra <= 1e-4) return;
        const straight = span.segments.reduce((a, g) => a + g.length, 0);
        if (straight <= 1e-6) return;
        span.segments.forEach(g => {
            const share = extra * (g.length / straight);
            /* Capped at a third of the run. Past that a real rope stops being a
               shallow bow and starts being a coil on the floor, and an arc the
               size of the machine says less than a short one does. */
            const bow = Math.sqrt(Math.max(0, 3 * g.length * share) / 8);
            out.set(`${g.ropeId}:${g.index}`, Math.min(bow, g.length / 3));
        });
    });
    return out;
}

function ropePaths(w: World, force: Record<string, number> | null): RopeDrawing[] {
    const sag = sagOf(w);
    return ropesOf(w).map(rope => {
        const stops = rope.path.filter(s => partById(w, s.partId));
        if (stops.length < 2) return null;

        /* Drawn from the geometry the ENGINE solved, and from nothing else.
         *
         * `segmentsOf` gives the tangent point at each end of every run and,
         * for a stop the rope passes over, the arc between them — where it
         * lands, where it leaves, which way round. All of it exact. So the
         * rope is drawn by reading those out, and the picture cannot disagree
         * with the force any more, because they are the same numbers.
         *
         * What this replaces is three approximations that all predate the
         * wheel having a size: contact points taken from the PORT (`x ± r`,
         * only right on a vertical run), a snap that nudged a nearly-vertical
         * run into line, and an arc whose direction was guessed from whether
         * the wheel sat above its neighbours. Each was a reasonable stand-in
         * for a tangent nobody could compute. All three are now second
         * opinions about a question with one answer. */
        const runs = segmentsOf(w).filter(g => g.ropeId === rope.id);
        if (!runs.length) return null;
        let d = `M ${runs[0].pa.x} ${runs[0].pa.y}`;
        runs.forEach((g, i) => {
            /* Bowed if this run has rope to spare, straight if not. Downwards,
               because that is where a loose rope goes; on a vertical run the
               only way out is sideways, which is what a slack line hanging
               between two points one above the other actually does. */
            const slackHere = sag.get(`${rope.id}:${i}`) ?? 0;
            if (slackHere > 0.01) {
                const dx = g.pb.x - g.pa.x, dy = g.pb.y - g.pa.y;
                const L = Math.hypot(dx, dy) || 1;
                let px = dy / L, py = -dx / L;
                if (py > 0) { px = -px; py = -py; }
                if (Math.abs(py) < 1e-6) { px = Math.abs(px); }
                const cx = (g.pa.x + g.pb.x) / 2 + px * slackHere * 2;
                const cy = (g.pa.y + g.pb.y) / 2 + py * slackHere * 2;
                d += ` Q ${cx} ${cy} ${g.pb.x} ${g.pb.y}`;
            } else {
                d += ` L ${g.pb.x} ${g.pb.y}`;
            }
            // And round the wheel, from where it lands to where it leaves.
            const next = runs[i + 1];
            if (g.wrap && next) {
                const big = g.wrap.angle > Math.PI ? 1 : 0;
                const sweep = g.wrap.sense > 0 ? 1 : 0;
                d += ` A ${g.wrap.radius} ${g.wrap.radius} 0 ${big} ${sweep} ${next.pa.x} ${next.pa.y}`;
            }
        });

        /* Beside the rope's LONGEST run, at its middle, just clear of the
         * line. A label belongs next to the thing it names — parked off to the
         * left of the whole rope it was a caption with nothing under it — and
         * the longest run is the one with room beside it. Only the ropes a
         * reader has selected are labelled at all, which is what makes being
         * close to the rope safe: nothing is competing for the space. */
        let longest = -1;
        let mid: { x: number; y: number; rim: boolean } = { ...runs[0].pa, rim: false };
        let upright = true;
        runs.forEach(g => {
            if (g.length <= longest) return;
            longest = g.length;
            mid = { x: (g.pa.x + g.pb.x) / 2, y: (g.pa.y + g.pb.y) / 2, rim: false };
            upright = Math.abs(g.pb.x - g.pa.x) < Math.abs(g.pb.y - g.pa.y);
        });
        const at = upright ? { x: mid.x - 0.1, y: mid.y } : { x: mid.x, y: mid.y + 0.16 };

        /* A rope pulls each end TOWARDS the other, and which way that is, is
         * the thing a student gets wrong. Only the ends matter — an arrow on a
         * run that continues over a sheave points at the sheave, which says
         * nothing — so the first and last stops get one each. */
        const pulls: Pull[] = [];
        if (stops.length >= 2) {
            const head = { a: runs[0].pa, b: runs[0].pb };
            const last = runs[runs.length - 1];
            const tail = { a: last.pb, b: last.pa };
            [head, tail].forEach(({ a, b }) => {
                const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy);
                if (L > 1e-6) pulls.push({ at: { x: a.x, y: a.y }, dir: { x: dx / L, y: dy / L } });
            });
        }

        const open = openEndOf(w, rope);
        return {
            id: rope.id,
            d,
            pulls,
            anchor: upright ? ('end' as const) : ('middle' as const),
            /* Slack is a STATE, not a fault: the row is simply not doing
             * anything, which is what an absent force means. Read from the
             * rope's FIRST span — the end a reader reads from — and a rope
             * divided by something that grips could differ along its length,
             * which is why this is the span's answer and not the rope's. */
            /* SLACK is a fact about the rope, not about the solve.
             *
             * Read off the geometry: a rope is slack when there is more of it
             * than the run it spans, which is true or false whether or not the
             * bench has an answer. Taken from the FORCE table instead — "no
             * tension listed, so draw it limp" — every rope on a bench with no
             * equilibrium was drawn hanging loose, because a failed solve
             * publishes no tensions at all. A reader then sees slack rope
             * where the rope is plainly taut and stretched between two parts,
             * which is the drawing contradicting the bench's own lengths. */
            slack: mechanics.slackOf(w, rope) > 1e-4,
            tension: force ? force[spanId(rope.id, 0)] : undefined,
            rigid: !!rope.rigid,
            ends: [{ ...runs[0].pa, rim: false }, { ...runs[runs.length - 1].pb, rim: false }],
            at,
            openAt: open ? contact(w, open.at) : null,
        };
    }).filter(Boolean) as RopeDrawing[];
}

/** Push labels apart so none is written over another. */
function spread(ropes: RopeDrawing[]): RopeDrawing[] {
    const MIN = 0.26;
    // Top down, and each pushed below the one above it: y runs up.
    const sorted = [...ropes].sort((a, b) => b.at.y - a.at.y);
    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1], cur = sorted[i];
        if (Math.abs(cur.at.x - prev.at.x) > 1.2) continue;     // far apart across
        if (prev.at.y - cur.at.y < MIN) cur.at = { ...cur.at, y: prev.at.y - MIN };
    }
    return sorted;
}

/**
 * The BLOCKS on the bench: each shell, and how wide it is.
 *
 * A block is one or more sheaves in a shell. On this bench the sheaves are
 * separate parts bolted axle to axle — which is exactly right for the physics,
 * since a bolt makes them one body — but it left the drawing showing a lower
 * block as two pulleys joined by a stick, with the load hanging off the stick
 * a wheel's width from either wheel. The shell is the thing that is really
 * there, and a reader has to see it to believe the load is carried by both.
 *
 * Grouped by bolts between axles at the same height, so a pulley hanging from
 * a hook above it is not swept in. The span covers every wheel in the group
 * and every becket on one — see `SheaveData.becket`, which is where an odd
 * tackle's dead end is spliced and why a block is wider than its wheel.
 */
function blocksOf(w: World): { id: string; x0: number; x1: number; y: number; r: number; eyes: number[] }[] {
    const wheels = w.parts.filter(p => p.kind === 'sheave');
    const home = new Map(wheels.map(p => [p.id, p.id]));
    const find = (id: string): string => (home.get(id) === id ? id : find(home.get(id) as string));
    bondsOf(w).forEach(b => {
        if (b.hangs.portId !== 'axle' || b.holds.portId !== 'axle') return;
        const a = partById(w, b.hangs.partId), c = partById(w, b.holds.partId);
        if (!a || !c || a.kind !== 'sheave' || c.kind !== 'sheave') return;
        if (Math.abs(a.at.y - c.at.y) > 1e-6) return;
        home.set(find(a.id), find(c.id));
    });

    const groups = new Map<string, Part[]>();
    wheels.forEach(p => {
        const key = find(p.id);
        groups.set(key, [...(groups.get(key) ?? []), p]);
    });

    return [...groups.entries()].flatMap(([id, ps]) => {
        const eyes = ps.flatMap(p => {
            const b = (p.data as { becket?: number }).becket;
            return b === undefined ? [] : [p.at.x + b];
        });
        // A lone wheel with nothing beside it is not a block, it is a wheel.
        if (ps.length < 2 && !eyes.length) return [];
        const r = Math.max(...ps.map(sheaveR));
        const xs = [...ps.map(p => p.at.x), ...eyes];
        return [{
            id, r, y: ps[0].at.y,
            x0: Math.min(...xs) - r * 0.3,
            x1: Math.max(...xs) + r * 0.3,
            eyes,
        }];
    });
}

/**
 * Every line that is joined to a sheave's AXLE, and which way it heads off.
 *
 * A rope tied to an axle runs to the middle of the wheel, and the wheel's own
 * fill is painted over the rope layer — so the last radius of it was swallowed
 * and a rope tied to the centre looked like a rope that stopped at the rim.
 * These stubs are the same line drawn again on top, and nothing else: the
 * engine's contact point was the centre all along.
 */
function axleSpokes(w: World, p: Part): { to: { x: number; y: number }; colour: string }[] {
    const out: { to: { x: number; y: number }; colour: string }[] = [];
    bondsOf(w).forEach(b => {
        // Hanging FROM something, and holding something up, are both joins at
        // the axle. Only the first was drawn, so a weight bolted under a
        // sheave had the last radius of its strap painted over by the wheel
        // and appeared to be tied to the RIM — which is a different machine.
        if (b.hangs.partId === p.id) {
            const holds = partById(w, b.holds.partId);
            if (holds) out.push({ to: holds.at, colour: COLOUR.pulley });
            return;
        }
        if (b.holds.partId !== p.id || b.holds.portId !== 'axle') return;
        const hangs = partById(w, b.hangs.partId);
        if (hangs) out.push({ to: hangs.at, colour: hangs.kind === 'weight' ? COLOUR.weight : COLOUR.pulley });
    });
    ropesOf(w).forEach(rope => {
        rope.path.forEach((stop, i) => {
            if (stop.partId !== p.id || stop.portId !== 'axle') return;
            [rope.path[i - 1], rope.path[i + 1]].forEach(next => {
                const to = next && partById(w, next.partId);
                if (to) out.push({ to: to.at, colour: COLOUR.rope });
            });
        });
    });
    return out;
}

/** What a spring balance reads: the tension of the line it is spliced into. */
function ropeOn(w: World, force: Record<string, number> | null, partId: string): number | null {
    if (!force) return null;
    for (const span of spansOf(w)) {
        if (!span.segments.some(g => g.a.partId === partId || g.b.partId === partId)) continue;
        const T = force[span.id];
        if (T !== undefined) return T;
    }
    return null;
}

export function MechanicsScene2D(props: SceneProps): React.JSX.Element {
    const { world: w, force, problems, selectedId, selectedLinkId, first, joining } = props;

    /* How much bench is actually on screen.
     *
     * The room is six metres wide, but the element it is drawn in is whatever
     * the column happens to be — so fitting the room to the element leaves a
     * strip of nothing down each side, and the floor and the beam stop short
     * of the edges with bare page beyond them. Measuring the element and
     * widening the VIEW instead keeps one metre one metre, and the ground runs
     * off both sides the way ground does. */
    const box = useRef<SVGSVGElement | null>(null);
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);
    useEffect(() => {
        const el = box.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const watch = new ResizeObserver(() => {
            const r = el.getBoundingClientRect();
            if (r.width > 0 && r.height > 0) setSize({ w: r.width, h: r.height });
        });
        watch.observe(el);
        return () => watch.disconnect();
    }, []);
    // ── the view: the whole room, or a closer look at part of it ─────────────
    /* Defined here, before anything is drawn, from the room's own box; the
       zoomed box is that box scaled about (cx, cy) and kept inside it, so a pan
       never shows past the walls. */
    const roomBox = (): { x: number; y: number; w: number; h: number } => {
        const T = w.room.ceiling - w.room.floor + ROOM_ABOVE_CEILING + ROOM_BELOW_FLOOR;
        const seenW = size ? Math.max(w.room.width, (size.w / size.h) * T) : w.room.width;
        return { x: w.room.width / 2 - seenW / 2, y: w.room.floor - ROOM_ABOVE_CEILING, w: seenW, h: T };
    };
    const viewOf = (z: SceneZoom | null | undefined): { x: number; y: number; w: number; h: number } => {
        const b = roomBox();
        if (!z || z.k <= 1) return b;
        const vw = b.w / z.k, vh = b.h / z.k;
        const cx = Math.min(b.x + b.w - vw / 2, Math.max(b.x + vw / 2, z.cx));
        const cy = Math.min(b.y + b.h - vh / 2, Math.max(b.y + vh / 2, z.cy));
        return { x: cx - vw / 2, y: cy - vh / 2, w: vw, h: vh };
    };
    const view = viewOf(props.zoom);
    /** Screen px per drawing unit (a metre), as the SVG fits the view — meet. */
    const pxPerUnit = size ? Math.min(size.w / view.w, size.h / view.h) : 60;
    /** How much larger the motion and force cards are drawn than their metric
     *  size. 1 on a wide screen; on a phone, enough that a card's title (0.074 m
     *  of type) reads at 11 px — which shrinks back toward 1 as the reader zooms
     *  in, since the room then draws everything bigger. At most 3. */
    const tagScale = props.touchPx ? Math.min(3, Math.max(1, 11 / (0.074 * pxPerUnit))) : 1;
    /** The same for the drawing's own labels — a name, a tension, the ruler:
     *  on a phone, enough that 0.09 m of type reads at 9 px. The stylesheet
     *  multiplies each label's metric size by it (`--mb-ts`), and caps the ones
     *  written ON a part lower, so they stay inside it. */
    const textScale = props.touchPx ? Math.min(2.6, Math.max(1, 9 / (0.09 * pxPerUnit))) : 1;
    /** A target's radius: its size in metres, or a finger's, whichever is bigger. */
    const touchR = (metres: number): number => Math.max(metres, (props.touchPx ?? 0) / pxPerUnit);

    // ── pinch, pan and the long press ────────────────────────────────────────
    const pointers = useRef(new Map<number, { x: number; y: number }>());
    const gesture = useRef<
        /** `bare`: pressed on the room itself — a tap there (no move) deselects. */
        | { mode: 'pan'; start: { x: number; y: number }; z: SceneZoom; moved: boolean; bare: boolean }
        | { mode: 'pinch'; dist: number; anchor: { x: number; y: number }; z: SceneZoom }
        | null
    >(null);
    const press = useRef<{ timer: number; id: string; start: { x: number; y: number } } | null>(null);
    const clearPress = (): void => { if (press.current) window.clearTimeout(press.current.timer); press.current = null; };
    useEffect(() => clearPress, []);
    /** The view as a zoom, even when it is the whole room (k = 1). */
    const zoomNow = (): SceneZoom => {
        const b = roomBox();
        return { k: b.w / view.w, cx: view.x + view.w / 2, cy: view.y + view.h / 2 };
    };
    /** Screen px per drawing unit at k = 1: the SVG fits the room's box, meet,
     *  and a zoomed view is that box scaled, so at k it is this times k. */
    const basePx = (): number => {
        const b = roomBox();
        return size ? Math.min(size.w / b.w, size.h / b.h) : 60;
    };
    /** Drawing coordinates of a screen point, through the SVG's own matrix. */
    const toDrawing = (x: number, y: number): { x: number; y: number } => {
        const m = box.current?.getScreenCTM();
        if (!m) return { x: 0, y: 0 };
        const p = new DOMPoint(x, y).matrixTransform(m.inverse());
        return { x: p.x, y: p.y };
    };
    const INTERACTIVE = '.mbench-node, .mbench-port, .mbench-ropehit, .mbench-handle, .mbench-tail, .mbench-ropeend';
    /* Capture phase: a second finger must become a pinch before a part under
       it starts a drag, and a press on the empty room must be told from a tap. */
    const onDownCapture = (e: React.PointerEvent): void => {
        if (!props.onZoom) return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size === 2) {
            clearPress();
            const [a, b] = [...pointers.current.values()];
            gesture.current = {
                mode: 'pinch',
                dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
                anchor: toDrawing((a.x + b.x) / 2, (a.y + b.y) / 2),
                z: zoomNow(),
            };
            box.current?.setPointerCapture?.(e.pointerId);
            e.stopPropagation();
            return;
        }
        const target = e.target as Element;
        const node = target.closest?.('.mbench-node') as SVGGElement | null;
        if (node && e.pointerType !== 'mouse' && props.onPartLongPress) {
            const id = node.dataset.pid;
            if (id) {
                clearPress();
                press.current = {
                    id, start: { x: e.clientX, y: e.clientY },
                    timer: window.setTimeout(() => {
                        const p = press.current;
                        press.current = null;
                        if (p) { props.onPartLongPress?.(p.id); navigator.vibrate?.(15); }
                    }, 500),
                };
            }
        }
        if (!target.closest?.(INTERACTIVE)) {
            gesture.current = { mode: 'pan', start: { x: e.clientX, y: e.clientY }, z: zoomNow(), moved: false, bare: e.target === e.currentTarget };
        }
    };
    const onMoveCapture = (e: React.PointerEvent): void => {
        if (!props.onZoom || !pointers.current.has(e.pointerId)) return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const p = press.current;
        if (p && Math.hypot(e.clientX - p.start.x, e.clientY - p.start.y) > 8) clearPress();
        const g = gesture.current;
        if (!g) return;
        const r = box.current?.getBoundingClientRect();
        if (!r) return;
        const rc = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
        if (g.mode === 'pinch') {
            const [a, c] = [...pointers.current.values()];
            if (!a || !c) return;
            const dist = Math.max(1, Math.hypot(a.x - c.x, a.y - c.y));
            const mid = { x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 };
            const k = Math.min(6, Math.max(1, g.z.k * (dist / g.dist)));
            // The drawing point first under the fingers stays under them:
            // screen = centre of the element + (point − view centre) × px per unit.
            const ppu = basePx() * k;
            props.onZoom(k <= 1.001 ? null : { k, cx: g.anchor.x - (mid.x - rc.x) / ppu, cy: g.anchor.y - (mid.y - rc.y) / ppu });
            e.stopPropagation();
            return;
        }
        const dx = e.clientX - g.start.x, dy = e.clientY - g.start.y;
        if (!g.moved && Math.hypot(dx, dy) < 5) return;
        g.moved = true;
        // The whole room is already on screen: there is nowhere to pan to.
        if (g.z.k <= 1) return;
        // A finger does not pan: on a phone a stray drag on the empty room
        // slid the whole drawing sideways and up, which read as the bench
        // coming loose rather than as a view being moved. Two fingers still
        // pan as they zoom (the pinch above), and Fit puts it back. A mouse
        // or a pen drag still pans.
        if (e.pointerType === 'touch') return;
        const ppu = basePx() * g.z.k;
        props.onZoom({ k: g.z.k, cx: g.z.cx - dx / ppu, cy: g.z.cy - dy / ppu });
    };
    const onUpCapture = (e: React.PointerEvent): void => {
        if (!props.onZoom) return;
        pointers.current.delete(e.pointerId);
        clearPress();
        const g = gesture.current;
        if (g?.mode === 'pinch') {
            // Ends when either finger lifts; the other does nothing until it lifts too.
            if (pointers.current.size === 0) gesture.current = null;
            e.stopPropagation();
            return;
        }
        gesture.current = null;
        // A tap on the bare room, not a pan: deselect, as a mouse click there does.
        if (g?.mode === 'pan' && !g.moved && g.bare) props.onBackground();
    };

    // ── fit the machine ──────────────────────────────────────────────────────
    const hasSize = !!size;
    useEffect(() => {
        if (!props.onZoom || !props.fitKey || !size) return;
        if (!w.parts.length) { props.onZoom(null); return; }
        let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
        w.parts.forEach(p => {
            let rx = 0.15, ry = 0.15;
            try {
                rx = mechanics.extentAlong(w, p, { x: 1, y: 0 });
                ry = mechanics.extentAlong(w, p, { x: 0, y: 1 });
            } catch { /* a part with no extent: its point, and a little */ }
            x0 = Math.min(x0, p.at.x - rx); x1 = Math.max(x1, p.at.x + rx);
            y0 = Math.min(y0, p.at.y - ry); y1 = Math.max(y1, p.at.y + ry);
        });
        const MARGIN = 0.3;
        const b = roomBox();
        const k = Math.min(6, Math.max(1, Math.min(b.w / (x1 - x0 + 2 * MARGIN), b.h / (y1 - y0 + 2 * MARGIN))));
        // Drawing y runs down: y → ceiling + floor − y.
        const cy = w.room.ceiling + w.room.floor - (y0 + y1) / 2;
        props.onZoom(k <= 1.02 ? null : { k, cx: (x0 + x1) / 2, cy });
        // Only a new fitKey (or the first size) fits: a drag must not refit the view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.fitKey, hasSize]);

    const keepRef = (el: SVGSVGElement | null): void => {
        box.current = el;
        const out = props.svgRef;
        if (typeof out === 'function') out(el);
        else if (out) (out as { current: SVGSVGElement | null }).current = el;
    };
    const ropes = useMemo(() => ropePaths(w, force), [w, force]);
    const names = useMemo(() => tensionNames(w), [w]);

    /* Which ropes have their tension written on the bench: the one selected,
     * and the ones pulling on whatever part is selected.
     *
     * Every tension, all the time, is a bench covered in numbers that nobody
     * asked for — and on a block and tackle they land on top of each other.
     * Selecting a weight and being shown what is pulling on it is the question
     * a reader actually has. The table below the bench still lists them all. */
    /* Which pins are actually doing something. A part the reader has pinned
     * may later be bolted to a hook — after which the hook holds it and the
     * pin is decoration. Drawn faint, so the bench does not look like it is
     * being held in six places when it is being held in one. */
    const holding = useMemo(() => {
        const out = new Set<string>();
        if (!anyPinned(w)) return out;
        bodiesOf(released(w)).forEach(b => {
            if (b.axes.length) b.partIds.forEach(id => out.add(id));
        });
        return out;
    }, [w]);

    const lit = useMemo(() => {
        const out = new Set<string>();
        if (selectedLinkId) out.add(selectedLinkId);
        if (selectedId) {
            const body = bodiesOf(w).find(b => b.partIds.includes(selectedId));
            const mine = new Set(body?.partIds ?? [selectedId]);
            ropesOf(w).forEach(r => { if (r.path.some(x => mine.has(x.partId))) out.add(r.id); });
        }
        return out;
    }, [w, selectedId, selectedLinkId]);
    const flagged = new Set(problems.flatMap(p => p.partIds ?? []));
    const CEIL = w.room.ceiling, FLOOR = w.room.floor, WIDE = w.room.width;
    /**
     * Which way this bench is MEASURED.
     *
     * Two different problems get drawn here and they are read from opposite
     * ends. A machine hanging off the beam is read downward — how far below
     * the ceiling a block hangs, how much rope is left — and the ruler runs
     * down the wall from the ceiling because that is where everything in it
     * starts. A block on a ramp or a table with no wheel anywhere is the other
     * problem, and every textbook in the world draws it the same way: x along
     * the ground to the right, y up from it, origin at the bottom left. Asking
     * a student to read a slope's height as a depth below a ceiling that has
     * nothing hanging from it is asking them to convert for no reason.
     *
     * Tested on what the parts CAN DO, not on what they are called: a surface
     * is a part something can stand on, and a wheel is a part a rope wraps
     * round. Nothing here needs to know the words "table" or "sheave".
     */
    /* The ceiling is drawn only while something hangs from it — a pulley, or
       anything mounted on the beam. A bench of tables, wedges and blocks has
       nothing up there, and a hatched beam across the top of it said there was
       a structure to fasten to that the machine does not use. Only the DRAWING
       comes and goes: the ceiling is still where things stop. */
    /* Rope at half the weight on a bench with a table or a wedge. There the
       rope runs along a face a block's height away from it, beside the blocks
       and the small wheels fastened to the surface, and at the full weight it
       was as heavy as the edge of the slab it ran beside. */
    const onSurfaces = w.parts.some(p => !!defOf(p).supports);
    const ropeW = onSurfaces ? { line: 0.016, rim: 0.018 } : { line: W.line, rim: W.rim };
    const hasCeiling = useMemo(() => w.parts.some(p => !!defOf(p).onBeam
        || portsOf(w, p).some(q => defOf(p).ropeContact(p as never, q, p.at).wrapRadius)), [w]);
    const onTheGround = useMemo(() => readsUp(w), [w]);
    const TALL = CEIL - FLOOR + ROOM_ABOVE_CEILING + ROOM_BELOW_FLOOR;
    // Metres across that the element can show at the scale the height sets.
    const seen = size ? Math.max(WIDE, (size.w / size.h) * TALL) : WIDE;
    const left = WIDE / 2 - seen / 2;
    // The floor runs the whole way; the beam stops a twentieth short of each
    // end, so it reads as a beam in a room rather than as another floor.
    const beamIn = seen * 0.05;

    /** One part, drawn and clickable. Used twice: for the ground, under the
     *  ropes, and for everything else, over them. */
    /* ── where each tag goes ───────────────────────────────────────────────
     *
     * Clear of the machine. A tag stood straight above its part landed on the
     * wedge the part was sliding down, on the block beside it, or on another
     * tag; so each one tries places around its part — above, then up to either
     * side, then beside, then below, each a little further out — and takes the
     * first that is inside the bench and touches no block, no table or wedge
     * (the real outline, not its box) and no tag already placed. One that had
     * to move away is joined back to its part by a faint line. */
    const freshSpots = (() => {
        // Placed at the size they are drawn — `tagScale` — so a card grown to be
        // read on a phone is still kept off the blocks and off its neighbours.
        const wanted = [
            ...(props.motionTags ?? []).map(m => ({ key: `motion-${m.partId}`, partId: m.partId, w: 0.66 * tagScale, h: (0.05 * 2 + 0.098 * 3) * tagScale })),
            ...(props.forceTags ?? []).map(t => ({ key: `force-${t.partId}`, partId: t.partId, w: 0.92 * tagScale, h: (0.05 * 2 + 0.092 * (t.rows.length + 2)) * tagScale })),
        ];
        const out = new Map<string, Box & { moved: boolean }>();
        if (!wanted.length) return out;
        const solids = w.parts.filter(p => !!defOf(p).outline).map(p => defOf(p).outline?.(p as never, w) ?? []);
        const reach = (p: Part): { x: number; y: number } => ({
            x: mechanics.extentAlong(w, p, { x: 1, y: 0 }),
            y: mechanics.extentAlong(w, p, { x: 0, y: 1 }),
        });
        const blocks: Box[] = w.parts.filter(p => defOf(p).mass(p as never) > 0 && !defOf(p).supports).map(p => {
            const r = reach(p);
            return { x: p.at.x - r.x, y: p.at.y - r.y, w: r.x * 2, h: r.y * 2 };
        });
        const taken: Box[] = [];
        wanted.forEach(t => {
            const part = partById(w, t.partId);
            if (!part) return;
            const r = reach(part), g = 0.08, c = part.at;
            const tries: Box[] = [];
            [0, 0.25, 0.5, 0.8, 1.2].forEach(d => {
                tries.push({ x: c.x - t.w / 2, y: c.y + r.y + g + d, w: t.w, h: t.h });                   // above
                tries.push({ x: c.x + r.x + g + d, y: c.y + r.y + g, w: t.w, h: t.h });                   // up and right
                tries.push({ x: c.x - r.x - g - d - t.w, y: c.y + r.y + g, w: t.w, h: t.h });             // up and left
                tries.push({ x: c.x + r.x + g + d, y: c.y - t.h / 2, w: t.w, h: t.h });                   // right
                tries.push({ x: c.x - r.x - g - d - t.w, y: c.y - t.h / 2, w: t.w, h: t.h });             // left
                tries.push({ x: c.x - t.w / 2, y: c.y - r.y - g - d - t.h, w: t.w, h: t.h });             // below
            });
            const clear = (b: Box): boolean => b.x >= left && b.x + b.w <= left + seen
                && b.y >= FLOOR && b.y + b.h <= CEIL + ROOM_ABOVE_CEILING
                && !blocks.some(k => boxesOverlap(b, k))
                && !solids.some(o => boxHitsOutline(b, o))
                && !taken.some(k => boxesOverlap(b, k));
            const pick = tries.find(clear) ?? tries[0];
            taken.push(pick);
            out.set(t.key, { ...pick, moved: pick !== tries[0] });
        });
        return out;
    })();

    /* Once it MOVES, a tag stays where it was put relative to its part and goes
       with it. Re-placing every frame to dodge the others made tags jump from
       side to side as parts passed each other — a label that moves by itself
       reads as information. So the offsets are taken at the first frame of the
       run and kept until the bench is back at rest; tags may overlap meanwhile. */
    const frozenTags = useRef<Map<string, Box & { moved: boolean }> | null>(null);
    if (!props.inMotion) frozenTags.current = null;
    const tagSpots = (() => {
        if (!props.inMotion) return freshSpots;
        const frozen = frozenTags.current ?? (frozenTags.current = new Map());
        const out = new Map<string, Box & { moved: boolean }>();
        const partOf = new Map<string, string>([
            ...(props.motionTags ?? []).map(m => [`motion-${m.partId}`, m.partId] as [string, string]),
            ...(props.forceTags ?? []).map(t => [`force-${t.partId}`, t.partId] as [string, string]),
        ]);
        partOf.forEach((partId, key) => {
            const part = partById(w, partId);
            if (!part) return;
            let off = frozen.get(key);
            if (!off) {
                const b = freshSpots.get(key);
                if (!b) return;
                off = { ...b, x: b.x - part.at.x, y: b.y - part.at.y };
                frozen.set(key, off);
            }
            // The place is frozen; the size is today's — a zoom mid-run resizes
            // a phone's cards, and a stale size would draw the card at the wrong scale.
            const now = freshSpots.get(key);
            out.set(key, { ...off, w: now?.w ?? off.w, h: now?.h ?? off.h, x: part.at.x + off.x, y: part.at.y + off.y });
        });
        return out;
    })();

    /* The parts wearing a motion tag, whose mass is in the tag and so is not
       written on the part as well. */
    const tagged = new Set([
        ...(props.motionTags ?? []).map(m => m.partId),
        // A force tag carries the mass in its title too.
        ...(props.forceTags ?? []).map(t => t.partId),
    ]);
    const partNode = (p: Part): React.JSX.Element => (
                <g
                    key={p.id}
                    /* While a point is waiting for its other end, anything that can
                       be joined to ANYWHERE along it says so. */
                    className={`mbench-node${joining && props.picking && defOf(p).mountAt ? ' is-pickable' : ''}`}
                    data-pid={p.id}
                    onPointerDown={e => props.onPartPointerDown(p.id, e)}
                    onClick={() => props.onPartClick(p.id)}
                    onDoubleClick={() => props.onPartDoubleClick(p.id)}
                >
                    {/* A pin, drawn where it can never be mistaken for part of
                        the machine: a head and a shaft, at the part's shoulder.
                        Something being HELD is the single most important thing
                        to be able to see at a glance, because it is the whole
                        difference between the two answers the bench gives. */}
                    {p.pinned && <Pin p={p} idle={!holding.has(p.id)} />}
                    {p.kind === 'anchor' && <Anchor p={p} w={w} />}
                    {p.kind === 'sheave' && (
                        <Pulley
                            p={p}
                            /* Solid hub = something is joined at the axle,
                               hollow = the axle is free. The one thing about a
                               sheave you cannot otherwise see from across a
                               room: whether it is fixed or movable. */
                            axleJoined={w.links.some(l => (l.type === 'rope' ? l.path : [l.hangs, l.holds])
                                .some(s => s.partId === p.id && s.portId === 'axle'))}
                            spokes={axleSpokes(w, p)}
                            tails={freeTails(w, p)}
                            onTail={(portId, e) => props.onTailPointerDown({ partId: p.id, portId }, e)}
                        />
                    )}
                    {defOf(p).outline && <Surface p={p} w={w} />}
                    {p.kind === 'springBalance' && <Balance p={p} reads={ropeOn(w, force, p.id)} />}
                    {p.kind === 'weight' && (
                        <Weight
                            /* A block on a ramp stands ON the ramp, so it turns
                               with it. Drawn upright, its lower corner cut
                               through the face and its upper corner floated — a
                               picture of a block half buried in a hill. */
                            hideMass={tagged.has(p.id)}
                            tilt={(Math.atan2(frameOf(w, p).y, frameOf(w, p).x) * 180) / Math.PI}
                            p={p}
                            name={defOf(p).name(p as never, w)}
                            /* Loose means nothing is on it at all. A weight a
                               ROPE holds was still drawing its own hanger, in a
                               different colour, beside the rope — two short
                               lines a hair apart read as a kink, and a kinked
                               rope reads as a rope that is not vertical. */
                            /* And a block STANDING on a face hangs from
                               nothing, so it has no hook to show: the stub on a
                               block tilted on a ramp stuck out of its top like
                               an aerial. */
                            loose={!p.support && !w.links.some(l => (l.type === 'rope' ? l.path : [l.hangs, l.holds]).some(s => s.partId === p.id))}
                        />
                    )}
                    {p.kind === 'ropeEnd' && <Hand p={p} />}

                    {/* An annotation ABOUT a part, not another part: dotted and
                        thin, because at 0.045 solid it was heavier than the
                        sheave it marked and read as a second wheel drawn round
                        it. No alarm ring while joining — a part you are half
                        way through connecting has not gone wrong. */}
                    {!joining && (p.id === selectedId || flagged.has(p.id)) && (
                        /* A ring round a 2.6-metre table is a circle half the
                           bench across that marks nothing. Anything with a FACE
                           gets its face outlined instead — which is the shape a
                           reader is actually selecting. */
                        defOf(p).outline ? (
                            <polygon
                                points={(defOf(p).outline?.(p as never, w) ?? []).map(v => `${v.x},${v.y}`).join(' ')}
                                fill="none"
                                stroke={flagged.has(p.id) ? ALARM : HILITE}
                                strokeWidth={W.fine}
                                strokeDasharray={DASH.ring}
                                strokeLinejoin="round"
                            />
                        ) : (
                            <circle
                                cx={p.at.x} cy={p.at.y}
                                r={Math.max(defOf(p).extent(p as never).x, defOf(p).extent(p as never).y) + 0.16}
                                fill="none"
                                stroke={flagged.has(p.id) ? ALARM : HILITE}
                                strokeWidth={W.hair}
                                strokeDasharray={DASH.ring}
                                strokeLinecap="round"
                            />
                        )
                    )}
                    {/* Its name, on the part — drawn LAST.
                        It used to go first, and a pulley is painted after its
                        label, so the name went on the wheel and then the wheel
                        went on the name. Nothing showed. Order is the whole
                        of it: a label written on something has to be put down
                        after the something.
                        A bench with three pulleys on it and nothing to tell
                        them apart is a bench whose readouts cannot be read:
                        "the ceiling hook" three times over is three different
                        hooks and no way to know which row is which. A weight
                        carries its own name inside the block; everything else
                        gets it here. */}
                    {(() => {
                        /* Where the name goes is the PART's answer, not the
                           drawing's — see `PartKind.nameAt`. A kind that draws
                           its own says so by answering null. */
                        const spot = defOf(p).nameAt(p as never, w);
                        return spot && (
                            <text
                                className={`mbench-tagname${p.kind === 'sheave' ? ' is-onpart' : ''}${defOf(p).outline ? ' is-onslab' : ''}`}
                                x={spot.at.x} y={spot.at.y} transform={upright(spot.at.y)}
                                textAnchor={spot.anchor}
                                dominantBaseline="middle"
                            >{defOf(p).name(p as never, w)}</text>
                        );
                    })()}
                </g>
    );

    return (
        <svg
            ref={keepRef}
            className="mbench-scene"
            /* The engine's y runs UP; the SVG's runs down. One flip, below, turns
               the whole bench over — `y → CEIL + FLOOR − y` — so the ceiling is
               at FLOOR on the page and the view starts just above it. */
            viewBox={props.zoom ? `${view.x} ${view.y} ${view.w} ${view.h}` : `${left} ${FLOOR - ROOM_ABOVE_CEILING} ${seen} ${TALL}`}
            /* With pan on, a press on the room may be the start of a pan, so it
               deselects on release instead, and only if it did not move. */
            onPointerDown={e => { if (e.target === e.currentTarget && !props.onZoom) props.onBackground(); }}
            onPointerDownCapture={onDownCapture}
            onPointerMoveCapture={onMoveCapture}
            onPointerUpCapture={onUpCapture}
            onPointerCancelCapture={onUpCapture}
            style={props.onZoom ? { touchAction: 'none', ['--mb-ts' as string]: textScale } : undefined}
        >
            <defs>
                <linearGradient id="pb-block" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0" stopColor={COLOUR.weight} />
                    <stop offset="1" stopColor="var(--mb-load-2)" />
                </linearGradient>
                {/* A sheave is a turned disc, not a hole in the bench. Lit from
                    the top left, as the weights are, so the two read as objects
                    of the same kind. */}
                <radialGradient id="pb-sheave" cx="0.35" cy="0.7" r="0.85">
                    <stop offset="0" stopColor="var(--mb-sheave-hi)" />
                    <stop offset="1" stopColor="var(--mb-sheave-lo)" />
                </radialGradient>
                <pattern id="pb-hatch" width="0.22" height="0.22" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="0.22" stroke="currentColor" strokeWidth={W.hair} opacity="0.4" />
                </pattern>
                {/* A slab of oak, lit from above like everything else on this
                    bench. Top to bottom in user space rather than across the
                    shape's box: a wedge and a table are different shapes and
                    the light does not change direction between them. */}
                <linearGradient id="pb-slab" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0" stopColor="var(--mb-slab-hi)" />
                    <stop offset="0.45" stopColor="var(--mb-slab)" />
                    <stop offset="1" stopColor="var(--mb-slab-lo)" />
                </linearGradient>
                {/* Furniture casts a shadow; the room does not. It is the
                    cheapest way to say a table stands in front of the wall
                    rather than being a hole cut in it. */}
                <filter id="pb-slabshadow" x="-20%" y="-20%" width="150%" height="150%">
                    <feDropShadow dx="0.03" dy="-0.05" stdDeviation="0.045" floodColor="#000" floodOpacity="0.28" />
                </filter>
            </defs>

            {/* Everything below is drawn in the engine's coordinates, y up, and
                turned over once here. Text is turned back by the stylesheet. */}
            <g transform={`matrix(1 0 0 -1 0 ${CEIL + FLOOR})`}>

            {/* ── the room ─────────────────────────────────────────────── */}
            <g className="mbench-room">
                {hasCeiling && (
                    <>
                        <rect x={left + beamIn} y={CEIL} width={seen - beamIn * 2} height="0.3" fill="url(#pb-hatch)" />
                        <line
                            x1={left + beamIn} y1={CEIL} x2={left + seen - beamIn} y2={CEIL}
                            stroke="currentColor" strokeWidth={W.line} opacity="0.55"
                        />
                    </>
                )}
                <rect x={left} y={FLOOR - 0.5} width={seen} height="0.5" fill="url(#pb-hatch)" />
                <line x1={left} y1={FLOOR} x2={left + seen} y2={FLOOR} stroke="currentColor" strokeWidth={W.line} opacity="0.45" />
            </g>

            {/* ── how far is a metre ─────────────────────────────────────
                A tick a metre along the wall, so the bench has a size. A reader
                cannot otherwise tell whether a pulley is the size of a fist or
                a cartwheel, and every number on the right — 0.4 m of wheel,
                3.05 m of rope — is a length they have nothing to hold it
                against. Faint on purpose: it is a ruler leaning against the
                wall, not part of the machine.

                Two rulers, because `onTheGround` above says there are two ways
                to read this bench. Only the ruler changes — the engine's own
                coordinates always run UP from the floor, and nothing below this
                line knows or needs to. */}
            <g className="mbench-rule">
                {onTheGround ? (
                    <>
                        {/* y, up from the floor */}
                        <line x1={left + 0.06} y1={CEIL} x2={left + 0.06} y2={FLOOR} />
                        {Array.from({ length: Math.floor(CEIL - FLOOR) + 1 }, (_, k) => k).map(k => (
                            <g key={`y${k}`}>
                                <line x1={left + 0.06} y1={FLOOR + k} x2={left + 0.06 + (k ? 0.13 : 0)} y2={FLOOR + k} />
                                {k > 0 && <text x={left + 0.24} y={FLOOR + k} transform={upright(FLOOR + k)} dominantBaseline="middle">{k} m</text>}
                            </g>
                        ))}
                        {/* x, rightward along the floor. Its ticks hang BELOW
                            the floor line, in the band where only hatching is,
                            so a ruler never crosses the machine standing on
                            it. */}
                        <line x1={left} y1={FLOOR - 0.06} x2={left + seen} y2={FLOOR - 0.06} />
                        {Array.from({ length: Math.floor(seen) + 1 }, (_, k) => k).map(k => (
                            <g key={`x${k}`}>
                                <line x1={left + k} y1={FLOOR - 0.06} x2={left + k} y2={FLOOR - 0.06 - (k ? 0.13 : 0)} />
                                {k > 0 && <text x={left + k} y={FLOOR - 0.33} transform={upright(FLOOR - 0.33)} textAnchor="middle" dominantBaseline="middle">{k} m</text>}
                            </g>
                        ))}
                        {/* The origin, said once. Both rulers start here and a
                            reader should not have to infer it from two zeros
                            that are not written. On the row the x labels are
                            on and in the column the y labels are in, so it
                            reads as the first entry of both. */}
                        <text x={left + 0.24} y={FLOOR - 0.33} transform={upright(FLOOR - 0.33)} dominantBaseline="middle">0</text>
                    </>
                ) : (
                    <>
                        {Array.from({ length: Math.floor(CEIL - FLOOR) + 1 }, (_, k) => CEIL - k).map(y => (
                            <g key={y}>
                                <line x1={left + 0.06} y1={y} x2={left + 0.06 + (y === CEIL ? 0 : 0.13)} y2={y} />
                                {y < CEIL && <text x={left + 0.24} y={y} transform={upright(y)} dominantBaseline="middle">{CEIL - y} m</text>}
                            </g>
                        ))}
                        <line x1={left + 0.06} y1={CEIL} x2={left + 0.06} y2={FLOOR} />
                    </>
                )}
            </g>


            {/* ── the ground things stand on ─────────────────────────
                Tables and wedges, BEFORE the ropes. They were drawn with the
                other parts, after the ropes, which cost nothing while a slab
                was see-through hatching — and once it was solid wood a rope
                running to a pulley at a wedge's apex, which meets the wheel
                just inside the slab's outline, was painted over and the bench
                looked as if it had no rope at all. Chosen by what a part HAS,
                an outline, not by what it is called. */}
            {w.parts.filter(p => !!defOf(p).outline).map(partNode)}

            {/* ── the straps, UNDER the ropes ──────────────────────────
                Drawn after, the cyan hanger from a load up to its sheave lay
                exactly over the run of rope beside it and the rope appeared to
                change colour half way down. Nothing a rope passes behind
                should be painted on top of it. */}
            <g strokeLinecap="round" fill="none">
                {bondsOf(w).map(b => {
                    const hangs = partById(w, b.hangs.partId), holds = partById(w, b.holds.partId);
                    if (!hangs || !holds) return null;
                    const isWeight = hangs.kind === 'weight';
                    const colour = isWeight ? COLOUR.weight : COLOUR.pulley;
                    /* STRAIGHT, from one join point to the other. It was an L
                       — down from one part, then across to the other — which
                       read as a frame for a weight hung beside a wheel, but it
                       also ran to the holding part's CENTRE rather than to the
                       point it was joined at: bolted to a point on a wedge's
                       slope, the strap went down to the middle of the wedge's
                       base. A bolt joins two points, and a straight bar between
                       them is what it is. */
                    const from = portAt(w, b.hangs)?.at ?? hangs.at;
                    const to = portAt(w, b.holds)?.at ?? holds.at;
                    const len = Math.hypot(to.x - from.x, to.y - from.y);
                    // Across the bar, so an end plate is square to it at any angle.
                    const n = len > 1e-9 ? { x: -(to.y - from.y) / len, y: (to.x - from.x) / len } : { x: 1, y: 0 };
                    const stroke = b.id === selectedLinkId ? HILITE : colour;
                    /* A bolt is RIGID, and has to look it. Drawn as a thin line
                       in the part's own colour it was indistinguishable from a
                       rope — so it gets a plate at each END and a square cap,
                       which a rope never has. */
                    return (
                        <g
                            key={`f-${b.id}`}
                            className={joining ? undefined : 'mbench-ropehit'}
                            onClick={() => !joining && props.onLinkClick(b.id)}
                        >
                            <line
                                x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                                stroke={stroke}
                                strokeWidth={b.id === selectedLinkId ? W.rim : W.line}
                                strokeLinecap="butt"
                            />
                            {[from, to].map((e, k) => (
                                <line
                                    key={k}
                                    x1={e.x - n.x * 0.07} y1={e.y - n.y * 0.07} x2={e.x + n.x * 0.07} y2={e.y + n.y * 0.07}
                                    stroke={stroke}
                                    strokeWidth={W.line} strokeLinecap="butt"
                                />
                            ))}
                        </g>
                    );
                })}
            </g>

            {/* ── ropes, under the parts they run over ─────────────────── */}
            <g fill="none" strokeLinecap="round" strokeLinejoin="round">
                {ropes.map(r => (
                    /* The click goes on the GROUP. On the fat twin alone it
                       never arrived: the visible line is drawn after it and
                       took every click itself, having no handler of its own. */
                    <g key={r.id} className={joining ? undefined : 'mbench-ropehit'} onClick={() => !joining && props.onLinkClick(r.id)}>
                        {/* A fat invisible twin, because a 0.032 line is not
                            something anybody can click. */}
                        {!joining && <path d={r.d} stroke="transparent" strokeWidth="0.24" />}
                        {/* A rod is drawn heavier, square-ended and plated, for
                            the same reason a bolt is: it can push, and a line
                            that can stand a weight up on another has to be
                            distinguishable at a glance from one that cannot. */}
                        <path
                            className="mbench-rope"
                            d={r.d}
                            stroke={r.id === selectedLinkId ? HILITE : r.rigid ? COLOUR.anchor : COLOUR.rope}
                            strokeWidth={r.id === selectedLinkId ? ropeW.rim : r.rigid ? ropeW.rim : ropeW.line}
                            strokeLinecap={r.rigid ? 'butt' : 'round'}
                            strokeDasharray={r.slack ? DASH.slack : undefined}
                            opacity={props.pulling?.rewire?.ropeId === r.id ? 0.25 : r.slack ? 0.45 : 1}
                        />
                        {r.rigid && r.ends.map((e, k) => (
                            <line
                                key={k}
                                x1={e.x - 0.07} y1={e.y} x2={e.x + 0.07} y2={e.y}
                                stroke={r.id === selectedLinkId ? HILITE : COLOUR.anchor}
                                strokeWidth={W.line} strokeLinecap="butt"
                            />
                        ))}
                    </g>
                ))}
            </g>

            {/* ── the blocks ───────────────────────────────────────────
                A block is a SHELL carrying wheels, and the shell is one thing
                even when it carries three. Drawn here rather than inside each
                wheel so it lies under all of them — and because the sheaves of
                one block are separate parts, so no one of them could draw it.
                Without it a lower block was two pulleys and a stick, with the
                load hanging off the stick a wheel's width from either. */}
            {blocksOf(w).map(b => (
                <g key={b.id} className="mbench-block" pointerEvents="none">
                    <rect
                        x={b.x0} y={b.y - b.r * 0.42} width={b.x1 - b.x0} height={b.r * 0.84} rx={b.r * 0.42}
                        fill="var(--mb-sheave-lo)" stroke={COLOUR.pulley} strokeWidth={W.hair}
                    />
                    {/* The becket: an eye on the shell for the fall's dead end. */}
                    {b.eyes.map((x, i) => (
                        <circle key={i} cx={x} cy={b.y} r={b.r * 0.24} fill="none" stroke={COLOUR.pulley} strokeWidth={W.fine} />
                    ))}
                </g>
            ))}

            {/* ── the parts ────────────────────────────────────────────── */}
            {w.parts.filter(p => !defOf(p).outline).map(partNode)}

            {/* A rope that stops on a sheave, marked where it stops. */}
            {ropes.filter(r => r.openAt).map(r => (
                <circle
                    key={`open-${r.id}`}
                    cx={(r.openAt as { x: number; y: number }).x}
                    cy={(r.openAt as { x: number; y: number }).y}
                    r="0.11" fill="none" stroke={ALARM}
                    strokeWidth={W.hair} strokeDasharray={DASH.ring} strokeLinecap="round"
                />
            ))}

            {/* ── the joining points, only while joining ────────────────
                A bench covered in dots is a bench that looks like a circuit
                diagram. They appear when they are the thing you are choosing
                between, and not before. */}
            {joining && w.parts.flatMap(p => portsOf(w, p)).map(port => {
                const ref = { partId: port.partId, portId: port.id };
                // A point that is full, or cannot take this join, is not offered.
                if (!props.offered.has(portKey(ref))) return null;
                const picked = !!first && portKey(first) === portKey(ref);
                const need = joinNeed(w, ref);
                const at = tipOf(w, ref) ?? port.at;
                return (
                    <g key={portKey(ref)} className="mbench-port" onClick={() => props.onPort(ref)}
                        onPointerDown={e => props.onPortPointerDown?.(ref, e)}>
                        {/* A finger-sized target under a pin-sized dot — half
                            the spacing between ports, so a sheave's axle cannot
                            swallow the inner half of the rim beside it. */}
                        <circle cx={at.x} cy={at.y} r={touchR(0.15)} fill="transparent" />
                        <circle
                            cx={at.x} cy={at.y} r={Math.max(picked ? 0.085 : 0.062, (props.touchPx ? (picked ? 6 : 4.5) : 0) / pxPerUnit)}
                            /* Picked is solid; everything else is a ring, so
                               the one you have chosen is the only filled dot on
                               the bench. */
                            fill={picked ? PORT[need] : COLOUR.sheave}
                            stroke={PORT[need]}
                            strokeWidth={W.fine}
                        />
                    </g>
                );
            })}

            {/* ── every force, where it acts ─────────────────────────────
                Scaled together against the biggest one on the bench, so the
                arrows can be compared with each other — which is the whole
                reason to draw them all at once rather than one body at a
                time. Coloured by what each force IS, the same way the
                free-body diagram colours them. */}
            {props.arrows && (() => {
                /* On the MASSES only. A pulley, a hook, a rope end and a balance
                   each carry a fan of arrows too, and drawn together they buried
                   the blocks — which are what a force diagram of a machine is
                   about. Chosen by whether the part has any mass, so a wheel
                   given a mass is shown as one. Scaled among themselves, so the
                   blocks' arrows stay comparable with each other. */
                const onMasses = props.arrows.filter(a => {
                    const q = a.on ? partById(w, a.on) : undefined;
                    return !!q && defOf(q).mass(q as never) > 0;
                });
                if (!onMasses.length) return null;
                const big = Math.max(...onMasses.map(a => Math.hypot(a.vector.x, a.vector.y)));
                const unit = 0.55 / (big || 1);
                return onMasses.map((a, i) => {
                    const L = Math.hypot(a.vector.x, a.vector.y) * unit;
                    if (L < 0.05) return null;
                    const ux = a.vector.x / (L / unit) / 1, uy = a.vector.y / (L / unit) / 1;
                    /* FROM THE BLOCK, out of its own edge in the direction the
                       force acts — not from where the force was recorded as
                       acting. That point was a rope's attachment or a snapshot's
                       position, and it left a tension floating clear above a
                       hanging block or starting down the slope from one sliding.
                       Anchored on the part as it is drawn, every arrow touches
                       the block it acts on and travels with it while it runs. */
                    const q = partById(w, a.on as string) as Part;
                    const edge = mechanics.extentAlong(w, q, { x: ux, y: uy });
                    const x0 = q.at.x + ux * edge, y0 = q.at.y + uy * edge;
                    const x1 = x0 + ux * L, y1 = y0 + uy * L;
                    // A head to match the thinner shaft.
                    const nx = -uy, ny = ux, h = 0.045;
                    return (
                        <g key={`F-${i}`} className="mbench-force" data-kind={a.kind}>
                            <line x1={x0} y1={y0} x2={x1} y2={y1} />
                            <path d={`M ${x1 - ux * h * 2 + nx * h} ${y1 - uy * h * 2 + ny * h} L ${x1} ${y1} L ${x1 - ux * h * 2 - nx * h} ${y1 - uy * h * 2 - ny * h} Z`} />
                        </g>
                    );
                });
            })()}

            {/* ── what is accelerating, and how fast ─────────────────────
                An acceleration is the one thing a picture of a machine cannot
                show by itself — a bench about to run looks exactly like one at
                rest — so while it is paused every moving mass carries its own
                number. Drawn OPEN-headed and in the accent, so it can never be
                read as one more force: forces are what cause this, not what it
                is. */}
            {([
                ['a', 'mbench-accel', -1, props.motion],
                ['v', 'mbench-speed', +1, props.speed],
            ] as const).map(([tag, cls, side, set]) => set && !props.showForces && Object.entries(set).map(([id, a]) => {
                const part = partById(w, id);
                if (!part) return null;
                const e = defOf(part).extent(part as never);
                const ux = a.x / a.size, uy = a.y / a.size;
                /* SHORT, and close in. These sit on a bench that is already
                   carrying force arrows, rope labels and tensions; a long one
                   crossed half the drawing and its number landed on somebody
                   else's part. Length still grows with the quantity, so the
                   comparison between two of them survives — it just does so
                   within a third of the room it used to take. */
                const out = Math.abs(ux) * e.x + Math.abs(uy) * e.y + 0.04;
                const L = Math.min(0.30, 0.09 + 0.022 * a.size);
                const nx = -uy, ny = ux, h = 0.05;
                // Set aside from its twin, along the perpendicular: the two are
                // collinear on any bench where things fall straight down.
                const off = side * 0.07;
                const x0 = part.at.x + ux * out + nx * off, y0 = part.at.y + uy * out + ny * off;
                const x1 = x0 + ux * L, y1 = y0 + uy * L;
                /* No number at the tip any more: each moving part's tag says v
                   and a, with its name and mass, and the same figures written
                   again at the arrows were two copies to read past. The arrows
                   stay — which way, and how big against each other. */
                return (
                    <g key={`${tag}-${id}`} className={cls}>
                        <line x1={x0} y1={y0} x2={x1} y2={y1} />
                        <path d={`M ${x1 - ux * h * 2 + nx * h} ${y1 - uy * h * 2 + ny * h} L ${x1} ${y1} L ${x1 - ux * h * 2 - nx * h} ${y1 - uy * h * 2 - ny * h}`} />
                    </g>
                );
            }))}

            {/* The line of action USED to be drawn here, dashed and separate.
                It existed because a pulley had no size in the model: the force
                ran through the axle while the rope a reader saw went round the
                rim, and those were two different lines with only one of them on
                screen. The rope is tangent to the rim now and the force acts
                along that same tangent — one line, and it is already drawn. A
                second copy of it was clutter that said the model was
                approximating somewhere it no longer is. */}

            {/* ── which way the selected rope pulls ─────────────────────
                A rope pulls each end towards the other, and that is the step a
                student drops — they draw the tension away from the sheave as
                often as towards it. Shown only for the rope in hand, in the
                same colour as its number, so the arrow and the newtons read as
                one statement. */}
            {/* Not while the masses carry force tags: the tag lists T, and an
                arrow and a label for the same tension on the rope were one
                more thing on a bench meant to show only the forces on masses. */}
            {props.showForces && force && !(props.forceTags ?? []).length && ropes.filter(r => lit.has(r.id) && !r.slack).flatMap(r => r.pulls.map((p, k) => {
                const L = 0.3;
                const ax = p.at.x + p.dir.x * L, ay = p.at.y + p.dir.y * L;
                const nx = -p.dir.y, ny = p.dir.x;
                const head = 0.07;
                return (
                    <g key={`p-${r.id}-${k}`} className="mbench-pull">
                        <line x1={p.at.x} y1={p.at.y} x2={ax} y2={ay} />
                        <path
                            d={`M ${ax} ${ay} L ${ax - p.dir.x * head * 2 + nx * head} ${ay - p.dir.y * head * 2 + ny * head} L ${ax - p.dir.x * head * 2 - nx * head} ${ay - p.dir.y * head * 2 - ny * head} Z`}
                        />
                    </g>
                );
            }))}

            {/* ── what the selected rope is carrying ───────────────────── */}
            {props.showForces && force && !(props.forceTags ?? []).length && spread(ropes.filter(r => lit.has(r.id))).map(r => {
                const t = r.tension;
                /* Named by `tensionNames`, which is the same T₁/T₂ the
                 * variables list and every free-body diagram use. Numbering it
                 * here instead — and by ROPE rather than by span — was a second
                 * opinion about what a force is called, and two opinions about
                 * a name is how a reader ends up unable to match the number on
                 * the bench to the row in the table. */
                const name = names.get(spanId(r.id, 0)) ?? 'T';
                return (
                    <text
                        key={`t-${r.id}`}
                        className="mbench-tension"
                        x={r.at.x} y={r.at.y} transform={upright(r.at.y)}
                        textAnchor={r.anchor}
                        dominantBaseline="middle"
                    >{t === undefined ? `${name} slack` : `${name} ${fixed(t, 1)} N`}</text>
                );
            })}

            {/* ── a tag on each moving part ─────────────────────────────
                Just above the part, measured from how far it actually reaches
                upward — so a block turned on a slope does not have its own tag
                laid across it — and over everything else, because it is read
                while the bench is moving. */}
            {(props.motionTags ?? []).map(m => {
                const part = partById(w, m.partId);
                if (!part) return null;
                // Three rows: which part and how heavy, then how fast, then how
                // fast that is changing — wherever `tagSpots` found room.
                const spot = tagSpots.get(`motion-${m.partId}`);
                if (!spot) return null;
                const ROW = 0.098, PAD = 0.05;
                // The card is drawn at its metric size and scaled about its corner.
                const TAG_W = spot.w / tagScale, TAG_H = spot.h / tagScale;
                const x = spot.x, y = spot.y;
                // Rows from the TOP of the tag down, and y runs up.
                const row = (k: number): number => y + TAG_H - PAD - ROW * (k + 0.5);
                return (
                    <g key={`motion-${m.partId}`} className="mbench-motiontag" pointerEvents="none">
                        {spot.moved && (
                            <line
                                className="mbench-tagleader"
                                x1={part.at.x} y1={part.at.y}
                                x2={Math.max(x, Math.min(x + spot.w, part.at.x))} y2={Math.max(y, Math.min(y + spot.h, part.at.y))}
                            />
                        )}
                        <g transform={tagScale === 1 ? undefined : `translate(${x} ${y}) scale(${tagScale}) translate(${-x} ${-y})`}>
                        <rect x={x} y={y} width={TAG_W} height={TAG_H} rx="0.045" />
                        <text className="mbench-motiontag-title" x={x + 0.05} y={row(0)} transform={upright(row(0))} dominantBaseline="middle">
                            {m.name}<tspan className="mbench-motiontag-u"> · {kg(m.mass)}</tspan>
                        </text>
                        <text x={x + 0.05} y={row(1)} transform={upright(row(1))} dominantBaseline="middle">
                            <tspan className="mbench-motiontag-k">v</tspan> {fixed(m.v, 2)} <tspan className="mbench-motiontag-u">m/s</tspan>
                        </text>
                        <text x={x + 0.05} y={row(2)} transform={upright(row(2))} dominantBaseline="middle">
                            <tspan className="mbench-motiontag-k">a</tspan> {fixed(m.a, 2)} <tspan className="mbench-motiontag-u">m/s²</tspan>
                        </text>
                        </g>
                    </g>
                );
            })}

            {/* ── a force tag on each mass ───────────────────────────────
                Where the motion tag goes — the two are never on at once — with
                a row per force: its symbol, then its x and its y, down being
                positive as in every table on this bench. Taller the more forces
                there are, growing upward so the part stays uncovered. */}
            {(props.forceTags ?? []).map(t => {
                const part = partById(w, t.partId);
                if (!part) return null;
                const spot = tagSpots.get(`force-${t.partId}`);
                if (!spot) return null;
                const ROW = 0.092, PAD = 0.05;
                const TAG_W = spot.w / tagScale, TAG_H = spot.h / tagScale;
                const x = spot.x, y = spot.y;
                // Rows from the TOP of the tag down, and y runs up.
                const row = (k: number): number => y + TAG_H - PAD - ROW * (k + 0.5);
                /* Right edges of the two number columns, far enough apart that a
                   value as wide as −13.1 in one never runs into the next. */
                const colX = x + 0.58, colY = x + TAG_W - 0.05;
                return (
                    <g key={`force-${t.partId}`} className="mbench-motiontag mbench-forcetag" pointerEvents="none">
                        {spot.moved && (
                            <line
                                className="mbench-tagleader"
                                x1={part.at.x} y1={part.at.y}
                                x2={Math.max(x, Math.min(x + spot.w, part.at.x))} y2={Math.max(y, Math.min(y + spot.h, part.at.y))}
                            />
                        )}
                        <g transform={tagScale === 1 ? undefined : `translate(${x} ${y}) scale(${tagScale}) translate(${-x} ${-y})`}>
                        <rect x={x} y={y} width={TAG_W} height={TAG_H} rx="0.045" />
                        <text className="mbench-motiontag-title" x={x + 0.05} y={row(0)} transform={upright(row(0))} dominantBaseline="middle">
                            {t.name}<tspan className="mbench-motiontag-u"> · {kg(t.mass)}</tspan>
                        </text>
                        <text className="mbench-motiontag-u" x={colX} y={row(1)} transform={upright(row(1))} textAnchor="end" dominantBaseline="middle">x</text>
                        <text className="mbench-motiontag-u" x={colY} y={row(1)} transform={upright(row(1))} textAnchor="end" dominantBaseline="middle">y</text>
                        {t.rows.map((r, i) => (
                            <g key={i}>
                                <text className="mbench-motiontag-k" x={x + 0.05} y={row(i + 2)} transform={upright(row(i + 2))} dominantBaseline="middle">{r.label}</text>
                                <text x={colX} y={row(i + 2)} transform={upright(row(i + 2))} textAnchor="end" dominantBaseline="middle">{fixed(r.x, 1)}</text>
                                <text x={colY} y={row(i + 2)} transform={upright(row(i + 2))} textAnchor="end" dominantBaseline="middle">{fixed(r.y, 1)}</text>
                            </g>
                        ))}
                        </g>
                    </g>
                );
            })}

            {/* Resize handles on the corners of the part double-clicked: squares,
                not dots, so they do not read as somewhere to join. */}
            {props.resizing && (() => {
                const part = partById(w, props.resizing);
                if (!part) return null;
                return (defOf(part).handles?.(part as never, w) ?? []).map(h => (
                    <g
                        key={h.id}
                        className="mbench-handle"
                        onPointerDown={e => { e.stopPropagation(); props.onHandlePointerDown(part.id, h.id, e); }}
                        onClick={e => e.stopPropagation()}
                    >
                        <circle cx={h.at.x} cy={h.at.y} r={touchR(0.15)} fill="transparent" />
                        {(() => { const hs = Math.max(0.06, (props.touchPx ? 7 : 0) / pxPerUnit); return (
                        <rect
                            x={h.at.x - hs} y={h.at.y - hs} width={2 * hs} height={2 * hs} rx={hs / 3}
                            fill={COLOUR.sheave} stroke={PORT.optional} strokeWidth={W.line}
                        />); })()}
                    </g>
                ));
            })()}

            {/* The selected rope or rod's two ends, as grips: drag one to tie
                that end somewhere else. Not an end on a rope's own loose end,
                which is a part and already drags. */}
            {!joining && !props.pulling && props.onRopeEndPointerDown && (() => {
                const rope = w.links.find(l => l.id === selectedLinkId);
                if (!rope || rope.type !== 'rope' || rope.path.length < 2) return null;
                const ends: [PortRef, boolean][] = [[rope.path[0], false], [rope.path[rope.path.length - 1], true]];
                return ends.map(([ref, atEnd]) => {
                    const port = portsOf(w, partById(w, ref.partId) as M.Part).find(q => q.id === ref.portId);
                    if (!port || port.joins.snaps) return null;
                    const at = tipOf(w, ref) ?? port.at;
                    return (
                        <g key={`end-${atEnd}`} className="mbench-ropeend"
                            onPointerDown={e => { e.stopPropagation(); props.onRopeEndPointerDown?.(rope.id, atEnd, e); }}>
                            <circle cx={at.x} cy={at.y} r={touchR(0.15)} fill="transparent" />
                            <circle cx={at.x} cy={at.y} r={Math.max(0.07, (props.touchPx ? 6 : 0) / pxPerUnit)}
                                fill={COLOUR.sheave} stroke={HILITE} strokeWidth={W.line} />
                        </g>
                    );
                });
            })()}

            {/* A rope being pulled out of a pulley's rope piece: every point it
                could join, the one it would join now, and the rope itself from
                the tail's tip to the hand — or to that point, once it snaps. */}
            {props.pulling && (() => {
                const pl = props.pulling;
                const from = tipOf(w, pl.from);
                if (!from) return null;
                const end = pl.snap ? (tipOf(w, pl.snap.to) ?? pl.snap.at) : pl.to;
                return (
                    <g pointerEvents="none">
                        {pl.targets.map(s => {
                            const t = tipOf(w, s.to) ?? s.at;
                            return <circle key={portKey(s.to)} cx={t.x} cy={t.y} r={0.062} fill="none" stroke={PORT.optional} strokeWidth={W.fine} />;
                        })}
                        {pl.snap && (
                            <circle cx={end.x} cy={end.y} r={0.12} fill={PORT.optional} fillOpacity={0.3} stroke={PORT.optional} strokeWidth={W.line} />
                        )}
                        <line x1={from.x} y1={from.y} x2={end.x} y2={end.y} stroke={COLOUR.rope} strokeWidth={W.line} strokeLinecap="round" />
                    </g>
                );
            })()}

            {/* The drag, drawn as an intention and not as a fact. Last, so it
                lies over whatever it is being dragged past. */}
            {props.ghost && (() => {
                const part = partById(w, props.ghost.id);
                if (!part) return null;
                const half = defOf(part).extent(part as never);
                const { x, y } = props.ghost.at;
                /* Turned only if it will lie on a slope — and only the OUTLINE
                   turns. The line back to where the part is now stays where
                   it is: turned with the outline it pointed somewhere else. */
                const turn = props.ghost.angle ? `rotate(${(props.ghost.angle * 180) / Math.PI} ${x} ${y})` : undefined;
                const snap = props.ghost.snap;
                return (
                    <>
                    {/* Where it can join, while it is being carried — and the
                        one it will join, if it is let go now. */}
                    <g pointerEvents="none">
                        {props.ghost.targets?.map(s => (
                            <circle
                                key={`${portKey(s.from)}>${portKey(s.to)}`}
                                cx={(tipOf(w, s.to) ?? s.at).x} cy={(tipOf(w, s.to) ?? s.at).y} r={0.062}
                                fill="none" stroke={PORT.optional} strokeWidth={W.fine}
                            />
                        ))}
                        {snap && (
                            <circle cx={(tipOf(w, snap.to) ?? snap.at).x} cy={(tipOf(w, snap.to) ?? snap.at).y} r={0.12}
                                fill={PORT.optional} fillOpacity={0.3} stroke={PORT.optional} strokeWidth={W.line} />
                        )}
                    </g>
                    {/* No outline for a rope end: it is drawn where it is, from the preview. */}
                    {!props.ghost.preview && <g className="mbench-ghost" pointerEvents="none">
                        <g transform={turn}>
                            {part.kind === 'sheave'
                                ? <circle cx={x} cy={y} r={sheaveR(part)} />
                                : <rect x={x - half.x} y={y - half.y} width={half.x * 2} height={half.y * 2} rx={0.04} />}
                        </g>
                        <line x1={part.at.x} y1={part.at.y} x2={x} y2={y} />
                    </g>}
                    </>
                );
            })()}
            </g>
        </svg>
    );
}

function Anchor({ p, w }: { p: Part; w: World }): React.JSX.Element {
    const eye = portsOf(w, p)[0].at;
    const runs = !!(p.data as { slides?: boolean }).slides;
    return (
        <g>
            {/* A runner has wheels on the beam and a fixed hook does not, which
                is the only way to tell them apart from across a room — and the
                difference is the whole of what it does. */}
            {runs && (
                <>
                    <circle cx={p.at.x - 0.13} cy={p.at.y + 0.07} r="0.055" fill={COLOUR.sheave} stroke={COLOUR.anchor} strokeWidth={W.hair} />
                    <circle cx={p.at.x + 0.13} cy={p.at.y + 0.07} r="0.055" fill={COLOUR.sheave} stroke={COLOUR.anchor} strokeWidth={W.hair} />
                </>
            )}
            <rect x={p.at.x - 0.22} y={p.at.y - 0.04} width="0.44" height="0.1" rx="0.04" fill={COLOUR.anchor} />
            <path d={`M ${p.at.x} ${p.at.y - 0.04} L ${p.at.x} ${eye.y + 0.04}`} stroke={COLOUR.anchor} strokeWidth={W.fine} />
            <circle cx={eye.x} cy={eye.y} r="0.055" fill="none" stroke={COLOUR.anchor} strokeWidth={W.fine} />
        </g>
    );
}

function Pulley({ p, axleJoined, spokes, tails, onTail }: {
    p: Part;
    axleJoined: boolean;
    spokes: { to: { x: number; y: number }; colour: string }[];
    tails: string[];
    onTail: (portId: string, e: React.PointerEvent) => void;
}): React.JSX.Element {
    const r = sheaveR(p);
    const g = grooveR(p);
    return (
        <g>
            <circle cx={p.at.x} cy={p.at.y} r={r} fill="url(#pb-sheave)" stroke={COLOUR.pulley} strokeWidth={W.rim} />
            {/* The rope groove, turned into the face — where the rope really
                runs when it is deeper than the rim, a line for looks when not. */}
            <circle cx={p.at.x} cy={p.at.y} r={grooveR(p) < r ? grooveR(p) : r * 0.72} fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth={W.hair} />
            {spokes.map((sp, i) => {
                const dx = sp.to.x - p.at.x, dy = sp.to.y - p.at.y, L = Math.hypot(dx, dy) || 1;
                return (
                    <line
                        key={i}
                        x1={p.at.x} y1={p.at.y}
                        x2={p.at.x + (dx / L) * r} y2={p.at.y + (dy / L) * r}
                        stroke={sp.colour} strokeWidth={sp.colour === COLOUR.rope ? W.line : W.fine}
                        strokeLinecap="round"
                    />
                );
            })}
            <circle
                cx={p.at.x} cy={p.at.y} r={r * 0.2}
                /* Solid is METAL, hollow is the bench showing through. Not a
                   dark disc against a dark bore — on a dark bench those are the
                   same near-black and the hub stopped saying anything.
                   The RING matters as much as the fill: at this size a
                   half-black hairline round a 3 cm circle closes over the hole
                   it is drawn on, so a free axle read as a dark stud. A free
                   one gets a faint ring, a joined one a dark hub it cannot be
                   mistaken for. */
                fill={axleJoined ? 'var(--mb-sheave-lo)' : 'var(--mb-ground)'}
                stroke={axleJoined ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.22)'}
                strokeWidth={W.hair}
            />
            {/* A wheel is 16 cm across; nothing legible fits on its face, so
                its name is drawn beside it by the generic label — see
                `PartKind.nameAt`. Only a mass it actually has appears here, and
                it goes under the name. */}
            {/* A piece of rope wrapped over the top of a free wheel, its two tails
                hanging down: their tips are the wheel's attach points, and a
                rope is pulled out of one by dragging it — nothing to select
                first. Gone once a rope runs over the wheel. */}
            {tails.length > 0 && (
                <g>
                    <path
                        d={`M ${p.at.x - g} ${p.at.y} A ${g} ${g} 0 0 0 ${p.at.x + g} ${p.at.y}`}
                        fill="none" stroke={COLOUR.rope} strokeWidth={W.line} strokeLinecap="round"
                    />
                    {tails.map(id => {
                        const x = id === 'rimL' ? p.at.x - g : p.at.x + g;
                        return (
                            <g
                                key={id}
                                className="mbench-tail"
                                onPointerDown={e => { e.stopPropagation(); onTail(id, e); }}
                                onClick={e => e.stopPropagation()}
                            >
                                <line x1={x} y1={p.at.y} x2={x} y2={p.at.y - ROPE_PIECE_TAIL} stroke={COLOUR.rope} strokeWidth={W.line} strokeLinecap="round" />
                                <circle cx={x} cy={p.at.y - ROPE_PIECE_TAIL} r="0.13" fill="transparent" />
                                <circle cx={x} cy={p.at.y - ROPE_PIECE_TAIL} r="0.045" fill={COLOUR.sheave} stroke={COLOUR.rope} strokeWidth={W.fine} />
                            </g>
                        );
                    })}
                </g>
            )}
            {massOf(p) ? (
                <text className="mbench-mass" x={p.at.x + r + 0.06} y={p.at.y - r * 0.5} transform={upright(p.at.y - r * 0.5)} dominantBaseline="middle">
                    {kg(massOf(p))}
                </text>
            ) : null}
        </g>
    );
}

type Box = { x: number; y: number; w: number; h: number };

const boxesOverlap = (a: Box, b: Box): boolean =>
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** Does a box overlap a CONVEX outline — a table or a wedge? Separating axes:
 *  the box's own two, and each edge's normal; overlapping on every one of
 *  them is overlapping. */
function boxHitsOutline(b: Box, poly: { x: number; y: number }[]): boolean {
    if (poly.length < 3) return false;
    const corners = [{ x: b.x, y: b.y }, { x: b.x + b.w, y: b.y }, { x: b.x + b.w, y: b.y + b.h }, { x: b.x, y: b.y + b.h }];
    const axes = [{ x: 1, y: 0 }, { x: 0, y: 1 }, ...poly.map((p, i) => {
        const q = poly[(i + 1) % poly.length];
        return { x: -(q.y - p.y), y: q.x - p.x };
    })];
    return axes.every(ax => {
        const a = corners.map(c => c.x * ax.x + c.y * ax.y);
        const c = poly.map(v => v.x * ax.x + v.y * ax.y);
        return Math.max(...a) > Math.min(...c) && Math.max(...c) > Math.min(...a);
    });
}

/**
 * How much room the view gives above the ceiling and below the floor, in
 * metres. Above is kept SMALL — a band of empty sky over the beam pushed the
 * whole machine down the stage for nothing. Exported because the bench places
 * the run controls just under the ceiling as a share of the stage's height,
 * and it must use the same numbers the view is drawn with.
 */
/**
 * Does the wall's ruler read UP from the floor? It does unless something HANGS
 * from the ceiling — heights, the way a ramp problem is drawn, even with a
 * pulley on the wedge — and DOWN from the ceiling once a join reaches a hook on
 * the beam, the way a hanging pulley problem is. A hook with nothing joined to
 * it hangs nothing. Exported because every y the bench prints has to count the
 * way this ruler does, or the axes and the numbers beside them disagree.
 */
export function readsUp(w: World): boolean {
    return !w.links.some(l => (l.type === 'rope' ? l.path : [l.hangs, l.holds]).some(s => {
        const p = partById(w, s.partId);
        return !!p && !!defOf(p).onBeam;
    }));
}

export const ROOM_ABOVE_CEILING = 0.12;
export const ROOM_BELOW_FLOOR = 0.4;

/** The heaviest a block can be before it is just "as dark as they come". */
const HEAVY = 20;

/** The shortest block that can carry two lines of type without crowding. */
const TWO_LINES = 0.3;

function Weight({ p, name, loose, tilt, hideMass }: { p: Part; name: string; loose: boolean; tilt: number; hideMass?: boolean }): React.JSX.Element {
    const m = massOf(p);
    // Its SIZE, not its mass alone: a block standing on a face is as tall as a
    // pulley is wide — see `WEIGHT_ON_FACE_HALF`.
    const hw = WEIGHT_HALF_W, hh = defOf(p).extent(p as never).y;
    /* Two readings of the same fact, because one of them alone is hard to
       judge: a heavier block is TALLER — width is fixed, so height is mass —
       and it is also DARKER. Height compares two blocks side by side; colour
       says "this is a heavy one" at a glance without anything to compare to. */
    const deep = Math.min(1, m / HEAVY);
    const fill = `color-mix(in srgb, var(--mb-load-2) ${Math.round(deep * 100)}%, var(--mb-load))`;
    return (
        <g transform={tilt ? `rotate(${tilt} ${p.at.x} ${p.at.y})` : undefined}>
            {/* Only the stub: a weight bolted to something has its frame drawn
                in the layer beneath the ropes. */}
            {loose && <line x1={p.at.x} y1={p.at.y + hh} x2={p.at.x} y2={p.at.y + hh + 0.1} stroke={COLOUR.weight} strokeWidth={W.fine} strokeLinecap="round" />}
            {/* Lit from above, like the wheels and the slabs: lighter at the
                top, the block's own colour through the middle, a little deeper
                at the foot. Its own gradient because every block's colour is
                its own — heavier is darker — and inside the block's group, so
                a block turned on a slope is lit along its own height. */}
            <defs>
                <linearGradient id={`blk-${p.id}`} x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0" style={{ stopColor: `color-mix(in srgb, ${fill} 74%, white)` }} />
                    <stop offset="0.55" style={{ stopColor: fill }} />
                    <stop offset="1" style={{ stopColor: `color-mix(in srgb, ${fill} 80%, black)` }} />
                </linearGradient>
            </defs>
            <rect
                x={p.at.x - hw} y={p.at.y - hh} width={hw * 2} height={hh * 2} rx="0.025"
                fill={`url(#blk-${p.id})`} stroke="rgba(0,0,0,0.3)" strokeWidth={W.hair}
            />
            {/* The NAME always goes inside. It is how the readouts, the faults
                and a classroom all refer to this block, so it belongs on the
                block and never somewhere a reader has to trace a line back
                from.

                Its MASS goes inside too when the block is tall enough to carry
                two lines without crowding, and outside when it is not — a light
                block is a shallow one, and two lines squeezed into it read as
                one smudge. */}
            {/* While it runs its mass is in the tag above it, so the block
                carries its name alone. */}
            {hideMass ? (
                <text className="mbench-inname mbench-on-block" x={p.at.x} y={p.at.y} transform={upright(p.at.y)} textAnchor="middle" dominantBaseline="middle">
                    {name}
                </text>
            ) : 2 * hh >= TWO_LINES ? (
                <>
                    <text className="mbench-mass mbench-on-block" x={p.at.x} y={p.at.y + 0.065} transform={upright(p.at.y + 0.065)} textAnchor="middle" dominantBaseline="middle">
                        {kg(m)}
                    </text>
                    <text className="mbench-inname mbench-on-block" x={p.at.x} y={p.at.y - 0.075} transform={upright(p.at.y - 0.075)} textAnchor="middle" dominantBaseline="middle">
                        {name}
                    </text>
                </>
            ) : (
                <>
                    <text className="mbench-inname mbench-on-block" x={p.at.x} y={p.at.y} transform={upright(p.at.y)} textAnchor="middle" dominantBaseline="middle">
                        {name}
                    </text>
                    {/* Beside a hanging block, whose rope leaves from its top.
                        ABOVE one standing on a face: its rope leaves from its
                        side, along the face, straight through a label written
                        there. Above is away from the face and clear of it. */}
                    {p.support ? (
                        /* Centred on the block, so it reads as the block's. */
                        <text className="mbench-mass" x={p.at.x} y={p.at.y + hh + 0.07} transform={upright(p.at.y + hh + 0.07)} textAnchor="middle" dominantBaseline="middle">
                            {kg(m)}
                        </text>
                    ) : (
                        <text className="mbench-mass" x={p.at.x + hw + 0.06} y={p.at.y} transform={upright(p.at.y)} dominantBaseline="middle">
                            {kg(m)}
                        </text>
                    )}
                </>
            )}
        </g>
    );
}

function Hand({ p }: { p: Part }): React.JSX.Element {
    const pull = pullOf(p);
    return (
        <g>
            <circle cx={p.at.x} cy={p.at.y} r="0.15" fill={COLOUR.sheave} stroke={COLOUR.hand} strokeWidth={W.fine} />
            <path d={`M ${p.at.x - 0.08} ${p.at.y} L ${p.at.x + 0.08} ${p.at.y}`} stroke={COLOUR.hand} strokeWidth={W.fine} />
            {/* To the side, for the same reason a weight's name is: below, it
                met the label of whatever was hanging next to it. */}
            <text className="mbench-name" x={p.at.x + 0.26} y={p.at.y} transform={upright(p.at.y)} dominantBaseline="middle">
                {pull === undefined ? 'Hold' : `${fixed(pull, 1)} N`}
            </text>
        </g>
    );
}

/**
 * A table or a ramp: a slab under a line.
 *
 * The line is the FACE — the thing the physics is about, and the thing a block
 * sits on — so it stays the crisp edge. Roughness is drawn on the face itself:
 * a smooth one is a plain line, a rough one carries teeth, and the coefficient
 * is written at the lower end where nothing else is.
 *
 * The slab beneath it used to be hatched like the floor and the beam, which is
 * this bench's word for "the world, not a part of the machine" — and that was
 * the wrong word. A table IS a part: it comes out of the rail, it has a
 * friction you set, you can delete it. Drawn in the room's language it read as
 * scenery, and a reader listing what the machine is made of left it out. So it
 * gets a material of its own — oak, warm where the room is cold — and a
 * shadow, which is what separates a thing standing in the room from a hole in
 * its wall.
 */
function Surface({ p, w }: { p: Part; w: World }): React.JSX.Element {
    const shape = defOf(p).outline?.(p as never, w) ?? [];
    /* Each face its own roughness, and the base its own grip on the floor —
       the base is fixed unless the reader has let it slip. */
    const muOf = (face: string): number =>
        (p.data as Record<string, number | undefined>)[`mu${face.charAt(0).toUpperCase()}${face.slice(1)}`] ?? 0;
    const grip = (p.data as { floorGrip?: number | 'fixed' }).floorGrip ?? 'fixed';
    const points = shape.map(v => `${v.x},${v.y}`).join(' ');
    const faces = defOf(p).faces?.(p as never, w) ?? [];
    return (
        <g>
            {/* Its own outline rather than a box: a wedge is a triangle, a
                table is a trapezium, and one polygon draws both without either
                kind telling the drawing what it is. */}
            <polygon
                points={points} fill="url(#pb-slab)" filter="url(#pb-slabshadow)"
                stroke={COLOUR.slab} strokeWidth={W.line} strokeLinejoin="round"
            />
            {/* The angle at each foot, where a slope meets the floor — the θ in
                g sin θ, so the one number a reader of a ramp problem needs to
                see on the ramp and not only in a slider. An arc from the floor
                up to the side, and its size beside it, inside the wood. An
                upright side is a table's, and a right angle marked on every
                table is a label that says nothing. */}
            {shape.length >= 3 && (() => {
                const { left, right } = p.data as { left?: number; right?: number };
                const bl = shape[0], br = shape[shape.length - 1];
                const R = 0.3;
                const feet: { at: { x: number; y: number }; theta: number | undefined; side: 1 | -1 }[] = [
                    { at: bl, theta: left, side: 1 },
                    { at: br, theta: right, side: -1 },
                ];
                return feet.map(({ at, theta, side }, i) => {
                    if (theta === undefined || Math.abs(theta - Math.PI / 2) < 0.01) return null;
                    const end = { x: at.x + side * R * Math.cos(theta), y: at.y + R * Math.sin(theta) };
                    const mid = { x: at.x + side * (R + 0.16) * Math.cos(theta / 2), y: at.y + (R + 0.16) * Math.sin(theta / 2) };
                    return (
                        <g key={i} className="mbench-angle" pointerEvents="none">
                            <path
                                d={`M ${at.x + side * R} ${at.y} A ${R} ${R} 0 0 ${side > 0 ? 1 : 0} ${end.x} ${end.y}`}
                                fill="none" stroke={COLOUR.slab} strokeWidth={W.fine}
                            />
                            <text className="mbench-name mbench-onslab" x={mid.x} y={mid.y} transform={upright(mid.y)} textAnchor="middle" dominantBaseline="middle">
                                {((theta * 180) / Math.PI).toFixed(0)}°
                            </text>
                        </g>
                    );
                });
            })()}
            {/* Roughness, on the faces that have it: a smooth face is a plain
                edge, a rough one carries teeth. */}
            {faces.map(f => {
                const mu = muOf(f.id);
                const sup = mu > 0 ? defOf(p).supports?.(p as never, w, f.id) : undefined;
                if (!sup) return null;
                const a = sup.seat(-sup.half, 0.028), b = sup.seat(sup.half, 0.028);
                // Its value inside the wood, level with the middle of the face.
                const at = sup.seat(0, -0.22);
                return (
                    <g key={f.id}>
                        {/* Fine and close: a grain along the face rather than a
                            row of beads. Rougher is still denser. */}
                        <line
                            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                            stroke={COLOUR.slab} strokeWidth={0.016}
                            strokeDasharray={`0.012 ${Math.max(0.018, 0.045 - mu * 0.03)}`} strokeLinecap="round"
                        />
                        <text className="mbench-name mbench-onslab" x={at.x} y={at.y} transform={upright(at.y)} textAnchor="middle" dominantBaseline="middle">μ {mu.toFixed(2)}</text>
                    </g>
                );
            })}
            {/* The base. Nothing for fixed — every ramp in a book is — and a
                word for a base let slip, because a wedge that can move looks
                exactly like one that cannot until the clock runs. */}
            {grip !== 'fixed' && shape.length >= 3 && (
                <g pointerEvents="none">
                    {grip > 0 && (
                        <line
                            x1={shape[0].x} y1={p.at.y + 0.035} x2={shape[shape.length - 1].x} y2={p.at.y + 0.035}
                            stroke={COLOUR.slab} strokeWidth={W.fine}
                            strokeDasharray={`0.02 ${Math.max(0.03, 0.1 - grip * 0.09)}`} strokeLinecap="round"
                        />
                    )}
                    <text className="mbench-name mbench-onslab" x={p.at.x} y={p.at.y + 0.15} transform={upright(p.at.y + 0.15)} textAnchor="middle" dominantBaseline="middle">
                        {grip > 0 ? `base μ ${grip.toFixed(2)}` : 'smooth base'}
                    </text>
                </g>
            )}
        </g>
    );
}

/**
 * A spring balance: a dial spliced into a line, reading what it carries.
 *
 * It shows its number whether or not it is selected, because that is the only
 * thing it is for — a balance whose face you have to click to read is a part
 * with no purpose on the bench.
 */
function Balance({ p, reads }: { p: Part; reads: number | null }): React.JSX.Element {
    const h = BALANCE_HALF;
    const { x, y } = p.at;
    /* A spring SCALE, drawn as the thing a lab hands you: a narrow casing with
       a slot down it, graduations beside the slot, and a pointer riding in it
       that sits where the spring has stretched to. It used to be a square with
       a number in it, which said "a reading" and nothing about how a balance
       arrives at one — and a student reads a real balance off its scale, not
       off a display.

       The casing is inside the part's extent (half-width 0.13, half-height
       0.22), so what is drawn and what is selected, dragged and collided with
       are still the same shape. */
    const top = y + h - 0.05, bottom = y - h + 0.05;
    const run = top - bottom;
    /* Two balances, as a lab keeps them: a 100 N one, and a 1000 N one for
       anything heavier. Ten marks either way, so each is 10 N or 100 N — a
       count a reader can do at a glance. Past 1000 N the pointer stops at the
       bottom of the scale, and the number beside it still says how far over. */
    const full = (reads ?? 0) <= 100 ? 100 : 1000;
    const frac = reads === null ? 0 : Math.max(0, Math.min(1, reads / full));
    const pointer = top - frac * run;
    const TICKS = 10;
    return (
        <g>
            {/* The hooks: from each end of the casing out to the port the
                ropes are tied to. */}
            <line x1={x} y1={y + h} x2={x} y2={top + 0.02} stroke={COLOUR.hand} strokeWidth={W.fine} strokeLinecap="round" />
            <line x1={x} y1={bottom - 0.02} x2={x} y2={y - h} stroke={COLOUR.hand} strokeWidth={W.fine} strokeLinecap="round" />
            {/* The casing. */}
            <rect
                x={x - 0.075} y={bottom - 0.02} width="0.15" height={run + 0.04} rx="0.03"
                fill={COLOUR.sheave} stroke={COLOUR.hand} strokeWidth={W.fine}
            />
            {/* The slot the pointer runs in. */}
            <line x1={x + 0.02} y1={top} x2={x + 0.02} y2={bottom} className="mbench-scale-slot" />
            {/* Graduations: a long one every half of the range, short between. */}
            {Array.from({ length: TICKS + 1 }, (_, k) => {
                const ty = top - (k / TICKS) * run;
                const long = k % 5 === 0;
                return (
                    <line
                        key={k} className="mbench-scale-tick"
                        x1={x - 0.055} y1={ty} x2={x - 0.055 + (long ? 0.055 : 0.03)} y2={ty}
                    />
                );
            })}
            {/* The pointer: where the spring has stretched to. */}
            {reads !== null && (
                <path
                    className="mbench-scale-pointer"
                    d={`M ${x - 0.005} ${pointer} L ${x + 0.06} ${pointer - 0.022} L ${x + 0.06} ${pointer + 0.022} Z`}
                />
            )}
            {/* The reading, OUTSIDE, with its unit — one piece of text, so the
                number is never separated from what it counts. */}
            <text className="mbench-reading" x={x + 0.13} y={y} transform={upright(y)} dominantBaseline="middle">
                {reads === null ? '—' : `${fixed(reads, 1)} N`}
            </text>
        </g>
    );
}

/**
 * A push-pin, stuck into the part's shoulder.
 *
 * It was a line and a circle, and at bench scale the line all but vanished:
 * what a reader saw was a dot, which says nothing. Being HELD is the single
 * most important thing to see at a glance — it is the whole difference between
 * the two answers this bench gives — so it is drawn as the thing it is named
 * after: a sharp point going INTO the part, a tapered spike, a collar, and a
 * round head to press on. The point is what carries the meaning; the head is
 * what makes it legible as a pin rather than a spike.
 */
function Pin({ p, idle }: { p: Part; idle: boolean }): React.JSX.Element {
    const e = defOf(p).extent(p as never);
    // The tip bites the corner; the pin leans back out of the bench at 45°.
    const tx = p.at.x + e.x * 0.72, ty = p.at.y + e.y * 0.72;
    const ux = 0.7071, uy = 0.7071;          // along the pin, away from the part
    const nx = -uy, ny = ux;                  // across it
    const at = (d: number, w = 0): [number, number] =>
        [tx + ux * d + nx * w, ty + uy * d + ny * w];

    const [cx, cy] = at(0.115);               // collar
    const [hx, hy] = at(0.175);               // head
    const [ax, ay] = at(0.10, 0.032);
    const [bx, by] = at(0.10, -0.032);
    return (
        <g opacity={idle ? 0.35 : 1}>
            {/* The spike: a point at the part, widening to the collar. */}
            <path d={`M ${tx} ${ty} L ${ax} ${ay} L ${bx} ${by} Z`} fill={COLOUR.hand} stroke="none" />
            {/* The shaft between collar and head. */}
            <line x1={cx} y1={cy} x2={hx} y2={hy} stroke={COLOUR.hand} strokeWidth={0.045} strokeLinecap="butt" />
            {/* The head you press on. */}
            <circle cx={hx} cy={hy} r="0.062" fill={COLOUR.hand} stroke="rgba(0,0,0,0.25)" strokeWidth={W.hair} />
        </g>
    );
}
